/* saturn-customizer.js — the Door D two-panel personalization workspace.
 *
 * Successor to saturn-configurator.js (2026-07-17, same day): the composer
 * core (per-line Ludlow inventory validation, canvas line-measure gate, web
 * font loading, proof minting) carries over; new here:
 *  - true-format interior emulator (5x7 / 7x5 per card_design.format), red ink
 *  - message placement as a CUSTOMER CHOICE (twoThirds | center), from catalog
 *  - local draft autosave (design-agnostic → powers the design-swap flow)
 *  - review modal enforcing the no-proof policy (approved == printed);
 *    confirmation stamps _lp_reviewed before the real form submits
 *  - own qty stepper + live pricing via SaturnPricing.computePricing
 *  - ?swatches=1 ink-pick mode (owner picks the red against a printed sample)
 *
 * The page owns its plain hidden /cart/add form — no Dawn machinery here, so
 * this file stamps _door: "D" itself.
 *
 * 2026-09-12 (ROADMAP #138 + #139): the line RULES now come from the built
 * composing core, window.SaturnComposerCore (assets/saturn-composer-core.js,
 * generated from services/composer/core) — card measure per format (4″ / 5.5″)
 * as two spliced slugs, the type case checked per slug, customer wording, and
 * the proof payload (bedPica + lineSlugs) the server re-checks. This file
 * keeps the DOM, the docking (one DOM, two arrangements on the 900px query),
 * drafts, proofs, beacons and Klaviyo; it no longer measures anything itself.
 */
(function () {
  'use strict';

  // iOS zooms the page when a field under 16px takes focus, and the card's line fields are true-size type —
  // the composer then sits zoomed and cut off (owner 2026-10-09). maximum-scale stops the auto-zoom; iOS
  // still honours pinch zoom (it ignores maximum-scale for that since iOS 10). Android never auto-zooms,
  // and maximum-scale WOULD block pinch there, so iOS only.
  if (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    var viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta && !/maximum-scale/.test(viewportMeta.content)) viewportMeta.content += ', maximum-scale=1';
  }

  // Public ingest host: fronts ONLY these two endpoints (everything else on it
  // is 404), so storefront code never names the planner's own hostname.
  var PROOF_URL = 'https://saturn-proof.vercel.app/api/personalization/proof';
  var BEACON_URL = 'https://saturn-proof.vercel.app/api/personalization/beacon';
  // Proof draft (2026-09-03): POST saves, GET /<token> restores, /<token>/image
  // is the PNG the proof email embeds. Same ingest host as proof + beacon.
  var DRAFT_URL = 'https://saturn-proof.vercel.app/api/personalization/draft';
  var PROOF_EMAIL_KEY = 'sp-lp-proof-email-v1';   // last address she gave us, for prefill
  var PROOF_SAVED_KEY = 'sp-lp-proof-saved-v1';   // composition hash of the last saved proof
  var PROOF_OFFERED_KEY = 'sp-lp-proof-offered-v1'; // sessionStorage: once per session
  var PROOF_IDLE_MS = 45000;                       // touch devices: no exit intent, so idle stands in
  // Composition DEPTH gate on the AUTOMATIC triggers (ROADMAP #170, owner's
  // question 2026-09-17: "can we make the proof offer trigger later, after
  // they've composed?"). Measured answer: yes — the offer was firing at the
  // first VALID composition, not after composing. Lifetime customer offers were
  // 0/1/1/1/1/2/2/11/12/13/25 edits deep in-session (median 2); every fire at
  // two edits or fewer was refused or ignored, and the only one ever taken was
  // the deepest ever fired (25). `proofsMinted` is per PAGE LOAD, which is what
  // makes this also fix the returning composer: one browser was re-offered 10 s,
  // 90 s and 37 s into three consecutive sessions on 25/28/30 lifetime edits,
  // because the once-per-session key resets while the composition does not.
  // Five is mid-gap — the observed distribution jumps 2 → 11, so anything from
  // 3 to 10 partitions today's data identically; it is a dial, not a fit.
  // The explicit "Email me a proof" link is NOT gated: she asked.
  var PROOF_MIN_EDITS = 5;
  var PROOF_DPI = 240;                             // "full resolution": 5x7 → 1200x1680
  var PROOF_DEBOUNCE_MS = 1200;
  var PROOF_TIMEOUT_MS = 4000;
  var DRAFT_KEY = 'sp-letterpress-draft-v1';
  var VISITOR_KEY = 'sp-lp-visitor-v1';
  // First-touch attribution, persisted alongside the visitor id (see firstTouch).
  var ATTRIB_KEY = 'sp_attrib_v1';
  // Internal-tester declaration (2026-09-04) — see the block in makeBeacon.
  var INTERNAL_KEY = 'sp-lp-internal-v1';
  var PX_PER_PT = 96 / 72;
  // The blank card carries NO sample message (owner 2026-09-16: the ghost
  // text "in theory should help people, but in practice doesn't look great
  // and will confuse our older customers"). The "Start here" cue above line 1
  // and row 1's dashed outline remain the invitation to type. History: James
  // asked for placeholders 2026-09-10, the owner worded them 2026-09-12
  // ("Season's Greetings / & Happy Holidays / from the Saturn Press Family /
  // to Yours"), and withdrew them 2026-09-16.
  var SWATCHES = [
    { name: 'PMS 186', hex: '#C8102E' },
    { name: 'PMS Warm Red', hex: '#FF665E' },
    { name: 'PMS 185', hex: '#E4002B' },
    { name: 'PMS 199', hex: '#D50032' },
    { name: 'PMS 1795', hex: '#D22630' },
    { name: 'PMS 187', hex: '#A6192E' },
    { name: 'PMS 202', hex: '#862633' },
  ];

  function warn(msg) {
    if (window.console && console.warn) console.warn('[saturn-customizer] ' + msg);
  }

  function deviceMeta() {
    var meta = {};
    try {
      var w = window.innerWidth || 0;
      meta.vw = w < 600 ? 'sm' : w < 900 ? 'md' : 'lg'; // sm/md = the stacked layout
      meta.ptr = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ? 'coarse' : 'fine';
    } catch (e) { /* telemetry never breaks composing */ }
    return meta;
  }

  // Build stamp (ROADMAP #124a, 2026-09-10): which program build the visitor
  // met. Rendered by the section from snippets/saturn-build.liquid. It goes
  // FIRST in every pageview meta because the endpoint keeps the first 8 keys
  // only and drops the rest in iteration order.
  function buildMeta(root) {
    return { build: (root && root.getAttribute('data-sp-build')) || 'unstamped' };
  }

  function makeBeacon(root, catalogVersionFn) {
    function uuid() {
      return (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
        : String(Date.now()) + '-' + Math.random().toString(16).slice(2);
    }
    var canTrack = null; // null = consent unknown (buffer) · true = send · false = drop
    var visitorId = 'vis_' + uuid(); // ephemeral unless consent permits persistence
    var sessionId = 'ses_' + uuid();
    var queue = [], flushTimer = null, seen = {};

    function applyConsent() {
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (!cp || typeof cp.userCanBeTracked !== 'function') return;
        canTrack = !!cp.userCanBeTracked();
        if (canTrack) {
          try {
            var stored = localStorage.getItem(VISITOR_KEY);
            if (stored) visitorId = stored;
            else localStorage.setItem(VISITOR_KEY, visitorId);
          } catch (e) { /* private mode: id stays session-scoped */ }
          flush(); // release anything buffered while consent was unknown
        } else {
          queue = [];
          try {
            localStorage.removeItem(VISITOR_KEY);
            localStorage.removeItem(ATTRIB_KEY);
          } catch (e) {}
        }
      } catch (e) { /* telemetry never breaks composing */ }
    }
    try {
      if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) { if (!err) applyConsent(); }
        );
      }
      // Fires when the shopper answers the banner (grant OR revoke), and on
      // later changes from the preferences popup.
      document.addEventListener('visitorConsentCollected', applyConsent);
    } catch (e) { /* no consent API — canTrack stays unknown, nothing sends */ }

    // ── Internal-tester declaration (2026-09-04) ──────────────────────────────
    // The Door D funnel's `internal` flag is PROVENANCE — hand-declared, never
    // inferred from behaviour. It used to key on the visitor id, which fails
    // because the id is minted in localStorage: every fresh profile or cleared
    // browser mints one no list has seen. The planner now also links a visitor
    // to an internal IDENTITY via the address on a saved proof draft, but that
    // only fires if the tester saves a draft — and James and Fitz, who test at
    // the owner's ask, mostly won't.
    //
    // So: let a human declare THIS BROWSER internal, once. Load any composer
    // page with `?sp_internal=1` and every later session from this browser
    // emits `internal_declared`, which the watch reads as the declaration.
    // `?sp_internal=0` revokes it — needed so the same person can go back to
    // seeing exactly what a customer sees.
    //
    // Still provenance, not inference: a person deliberately opted in. Nothing
    // here looks at what the traffic DID.
    //
    // Two deliberate choices:
    //  - Persisted WITHOUT waiting on consent, unlike VISITOR_KEY/ATTRIB_KEY.
    //    It is not an identifier and it enables no new send — it only tags
    //    events already going out, and only ever REMOVES traffic from the
    //    customer counts. If consent is declined nothing sends at all, flag or
    //    no flag, so there is nothing to get wrong.
    //  - The URL declaration tags the session even when localStorage throws
    //    (private window), so a one-off private-mode test still lands right.
    var isInternal = false;
    try {
      var declared = new URLSearchParams(location.search).get('sp_internal');
      var declaredOn = (declared === '1' || declared === 'true');
      var declaredOff = (declared === '0' || declared === 'false');
      if (declaredOn) isInternal = true;
      try {
        if (declaredOn) localStorage.setItem(INTERNAL_KEY, '1');
        else if (declaredOff) localStorage.removeItem(INTERNAL_KEY);
        else isInternal = localStorage.getItem(INTERNAL_KEY) === '1';
      } catch (e) { /* private mode — the URL declaration still tags this session */ }
    } catch (e) { /* telemetry never breaks composing */ }
    // Buffers like any other event until consent resolves, then rides the first
    // batch. `once` is per page load, i.e. once per session id.
    if (isInternal) emit('internal_declared', null, { once: true });

    function flush() {
      clearTimeout(flushTimer); flushTimer = null;
      if (canTrack !== true || !queue.length) return;
      var body = JSON.stringify({
        visitorId: visitorId, sessionId: sessionId,
        productHandle: root.getAttribute('data-product-handle') || null,
        catalogVersion: catalogVersionFn ? catalogVersionFn() : null,
        events: queue.splice(0, 25),
      });
      try {
        // text/plain keeps the request CORS-simple — no preflight a
        // fire-and-forget beacon can't perform.
        if (navigator.sendBeacon) {
          navigator.sendBeacon(BEACON_URL, new Blob([body], { type: 'text/plain' }));
        } else {
          fetch(BEACON_URL, { method: 'POST', body: body, keepalive: true, headers: { 'Content-Type': 'text/plain' } });
        }
      } catch (e) { /* telemetry never breaks composing */ }
      if (queue.length) schedule(250);
    }
    function schedule(ms) { if (!flushTimer) flushTimer = setTimeout(flush, ms); }

    // First-touch attribution (2026-08-06). "Did people sail through" is only
    // half the Door D question once campaigns run; "which campaign sent the
    // people who sailed through" is the half that decides spend. Captured
    // FIRST-TOUCH and persisted next to the visitor id, because the visit
    // that converts is usually not the visit that was advertised to.
    //
    // Labels only, same rule as every other meta value: utm_* tokens and the
    // referrer HOST — never a full referrer URL, which can carry query text.
    // Truncated well under the endpoint's 60-char cap.
    //
    // ROADMAP #168 (2026-09-16) — three fixes, mirroring the copy in
    // snippets/saturn-resume-nudge.liquid. Read that one for the census that
    // motivated them; the rules here are identical and must stay identical.
    // This copy serves Door D (`pdp_loaded`, `opened`, `door_closed_landing`);
    // the snippet's serves `collection_viewed` and Door A/B/C.
    //
    // Click ids: the acquisition signal when nobody appended utm tags. `src`
    // takes the platform token (already mapped in trafficSource.ts), `med`
    // the PARAMETER NAME, so a derived source (`google / srsltid`) is legible
    // as derived. They reuse src/med rather than adding a key because the
    // beacon endpoint caps meta at 8 keys and drops the overflow in insertion
    // order — `pdp_loaded` (build·vw·ptr·format·edit + three) already sits at
    // exactly 8. Allowlist, so a new platform needs a line here.
    var CLICK_IDS = [
      ['gclid', 'google'], ['gbraid', 'google'], ['wbraid', 'google'],
      ['srsltid', 'google'], ['gad_source', 'google'], ['fbclid', 'facebook']
    ];
    /** Does this tuple actually say anything? `{}` is an absence, not an answer. */
    function hasAttrib(t) {
      if (!t || typeof t !== 'object') return false;
      for (var k in t) if (Object.prototype.hasOwnProperty.call(t, k)) return true;
      return false;
    }
    /** www. and the apex are the same site; a raw !== recorded same-site
     *  referrals as a SOURCE. */
    function bareHost(h) { return String(h || '').toLowerCase().replace(/^www\./, ''); }

    function firstTouch() {
      try {
        var stored = localStorage.getItem(ATTRIB_KEY);
        // ⚠ An EMPTY stored tuple is not a settled answer: `JSON.stringify({})`
        // is the truthy string '{}', so `if (stored) return` pinned a browser
        // to "no source" for good the first time it arrived untagged.
        // ⚠ Re-deriving past it makes this FIRST ATTRIBUTABLE TOUCH, not first
        // touch — a browser that arrives organically and returns on a Meta ad
        // now reports Meta. A non-empty tuple is still never overwritten.
        if (stored) {
          var parsed = JSON.parse(stored);
          if (hasAttrib(parsed)) return parsed;
        }
      } catch (e) { /* private mode — fall through and derive, just don't persist */ }
      var t = {};
      try {
        var q = new URLSearchParams(location.search);
        var src = q.get('utm_source'), med = q.get('utm_medium'), cmp = q.get('utm_campaign');
        if (src) t.src = String(src).slice(0, 40);
        if (med) t.med = String(med).slice(0, 40);
        if (cmp) t.cmp = String(cmp).slice(0, 40);
        if (!t.src) {
          for (var i = 0; i < CLICK_IDS.length; i++) {
            if (q.get(CLICK_IDS[i][0])) {
              t.src = CLICK_IDS[i][1];
              if (!t.med) t.med = CLICK_IDS[i][0];
              break;
            }
          }
        }
        if (!t.src && document.referrer) {
          var h = new URL(document.referrer).hostname;
          // Same-site navigation is not a source — leave it unattributed
          // rather than crediting the storefront for its own traffic. Store
          // the full host (m.facebook.com must survive), compare bare.
          if (h && bareHost(h) !== bareHost(location.hostname)) t.ref = h.slice(0, 40);
        }
      } catch (e) { /* attribution never breaks composing */ }
      // Persist first-touch only once tracking is affirmatively permitted —
      // deriving labels for the current batch is fine either way (they ride
      // or drop with the events), but nothing lands in storage without a yes.
      // Nothing EMPTY lands either: writing `{}` is what made one untagged
      // visit permanent.
      try { if (canTrack === true && hasAttrib(t)) localStorage.setItem(ATTRIB_KEY, JSON.stringify(t)); } catch (e) {}
      return t;
    }

    function emit(name, meta, opts) {
      if (canTrack === false) return; // tracking declined — drop, don't buffer
      if (opts && opts.once) { if (seen[name]) return; seen[name] = true; }
      queue.push({ e: name, t: Date.now(), m: meta || undefined });
      schedule(opts && opts.now ? 0 : 4000);
    }
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush();
    });
    return {
      emit: emit, flush: flush, firstTouch: firstTouch,
      // visitorId is read lazily via id() because consent resolution can
      // swap the ephemeral id for the persisted one after this IIFE returns.
      id: function () { return visitorId; },
      session: function () { return sessionId; },
      tracked: function () { return canTrack === true; },
    };
  }

  // ── Closed door: the wayfinding page, instrumented (#81, 2026-08-12) ──────
  // This function exists because of one line below it: init() used to `return`
  // the moment `data-door-open` was not "true", which meant a closed design's
  // page emitted NOTHING. Not a pageview, not attribution — the visit did not
  // happen as far as the program could tell.
  //
  // That was survivable while Door D traffic was organic. It is not survivable
  // with ads: a campaign is configured once and runs for weeks, while a door
  // opens and closes on INVENTORY, several times, on its own schedule. The
  // failure mode is silent and expensive — spend keeps buying clicks that land
  // on "temporarily unavailable", and nothing anywhere says so.
  //
  // Two events. `door_closed_landing` says someone arrived and why the door was
  // shut (a capacity pause and an inventory close are different problems with
  // different owners). `closed_offramp_click` says whether the wayfinding page
  // caught them — the off-ramp has been live since 2026-08-03 with no way to
  // tell whether it works.
  function closedDoorInit(root) {
    var beacon = makeBeacon(root, function () { return null; });
    beacon.emit(
      'door_closed_landing',
      // Device keys first: cleanMeta caps meta at 8 keys and drops the overflow
      // in iteration order (same ordering constraint as pdp_loaded).
      Object.assign(buildMeta(root), deviceMeta(), { reason: root.getAttribute('data-closed-reason') || 'unknown' }, beacon.firstTouch()),
      { once: true, now: true }
    );

    // Delegated, because the off-ramp cards are rendered by the THEME'S OWN
    // card-product snippet — markup we deliberately do not own and must not
    // depend on the shape of.
    root.addEventListener('click', function (ev) {
      try {
        var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
        if (!a || !root.contains(a)) return;
        // --family carries both grid classes, so it has to be tested first.
        // 2026-10-05 layout: .scz-cl-opts = the design's formats (family),
        // .scz-cl-show = the open-designs carousel (open_design).
        var to = a.closest('.scz-closed-grid--family, .scz-cl-opts') ? 'family'
          : a.closest('.scz-closed-all') ? 'see_all'
          : a.closest('.scz-closed-grid, .scz-cl-show') ? 'open_design'
          : null;
        if (!to) return;
        // A carousel tap the two-path sheet took over is not a click-through
        // (the sheet reports itself as sheet_open, src closed-door).
        if (ev.defaultPrevented && to === 'open_design') return;
        beacon.emit('closed_offramp_click', { to: to }, { now: true });
      } catch (e) { /* telemetry never breaks wayfinding */ }
    });
    closedCarousel(root);
  }

  // The open-designs hero carousel (2026-10-05): arrows step the slide and
  // repoint the link; a plain tap follows the link to that design's composer —
  // or, while the set_for_you switch has loaded the two-path sheet, opens the
  // sheet for that design (same as the Door D collection carousel). Prices for
  // the sheet come from #scz-cl-data, rendered beside the slides.
  function closedCarousel(root) {
    var stage = root.querySelector('.scz-cl-show');
    if (!stage) return;
    var link = stage.querySelector('#scz-cl-link');
    var imgs = [].slice.call(stage.querySelectorAll('.scz-cl-link img'));
    var name = stage.querySelector('#scz-cl-name');
    var i = 0;
    function show(n) {
      if (!imgs.length) return;
      imgs[i].classList.remove('on');
      i = (n + imgs.length) % imgs.length;
      imgs[i].classList.add('on');
      if (name) name.textContent = imgs[i].getAttribute('data-name');
      if (link) link.setAttribute('href', imgs[i].getAttribute('data-url'));
    }
    [].slice.call(stage.querySelectorAll('.scz-cl-btn')).forEach(function (b) {
      b.addEventListener('click', function () { show(i + parseInt(b.getAttribute('data-dir'), 10)); });
    });
    if (!link) return;
    // Capture on the link itself, so the delegated off-ramp beacon above sees
    // defaultPrevented when the sheet takes the tap.
    link.addEventListener('click', function (e) {
      var PS = window.SaturnPersonalizeSheet;
      if (!PS || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var data = {};
      try { data = JSON.parse((document.getElementById('scz-cl-data') || {}).textContent || '{}'); } catch (err) { return; }
      var img = imgs[i];
      var d = img && data[img.getAttribute('data-url')];
      var cfg = d && d.config && d.config.doors ? (d.config.doors[d.config.defaultDoor] || d.config.doors.D) : null;
      if (!cfg) return;
      e.preventDefault();
      var floor = cfg.minQty || 50, tiers = cfg.tiers || [], sur = cfg.unitSurchargeCents || 0;
      PS.open({
        source: 'closed-door',
        handle: img.getAttribute('data-handle'),
        design: img.getAttribute('data-name'),
        url: img.getAttribute('data-url'),
        image: d.image ? { src: d.image, alt: d.alt || '' } : { src: img.currentSrc || img.src, alt: '' },
        thumb: { src: img.currentSrc || img.src },
        price: {
          floor: floor,
          fromCents: tiers.length ? tiers[0].unitCents + sur : null,
          quote: function () {
            var SP = window.SaturnPricing;
            if (SP && SP.computePricing) {
              var r = SP.computePricing(cfg, floor);
              if (r && r.valid) return { qty: floor, totalCents: r.subtotalCents, unitCents: r.unitEffectiveCents };
            }
            if (!tiers.length) return null;
            var unit = tiers[0].unitCents + sur;
            return { qty: floor, totalCents: unit * floor, unitCents: unit };
          }
        },
        opener: link
      });
    });
  }

  function init() {
    var root = document.getElementById('saturn-customizer');
    if (!root) return;
    // Closed-door static page. It is a wayfinding page, not a composer — but it
    // is still a landing, and since 2026-08-12 it says so (see closedDoorInit).
    if (root.getAttribute('data-door-open') !== 'true') { closedDoorInit(root); return; }

    var els = {
      headerPrice: document.getElementById('scz-header-price'),
      draftState: document.getElementById('scz-draft-state'),
      atc: document.getElementById('scz-atc'),
      font: document.getElementById('scz-font'),           // typeface list (buttons)
      size: document.getElementById('scz-size'),           // size segmented control
      placement: document.getElementById('scz-placement'),
      placementHint: document.getElementById('scz-placement-hint'),
      qtyNote: document.getElementById('scz-qty-note'),
      headerQty: document.getElementById('scz-header-qty'),
      barMid: document.getElementById('scz-bar-mid'),
      barActions: document.getElementById('scz-bar-actions'),
      barMirror: document.getElementById('scz-bar-mirror'),
      dockQty: document.getElementById('scz-dock-qty'),
      dockAction: document.getElementById('scz-dock-action'),
      stylePanel: document.getElementById('scz-style-panel'),
      styleDock: document.getElementById('scz-style-dock'),
      styleSheet: document.getElementById('scz-style-sheet'),
      styleDone: document.getElementById('scz-style-done'),
      canvasCol: root.querySelector('.scz-canvas-col'),
      chipFace: document.getElementById('scz-chip-face'),
      chipSize: document.getElementById('scz-chip-size'),
      chipPlacement: document.getElementById('scz-chip-placement'),
      meterSlot: document.getElementById('scz-meter-slot'),
      cardLinks: document.getElementById('scz-card-links'),
      startCue: document.getElementById('scz-start-cue'),
      startCueText: document.getElementById('scz-start-cue-text'),
      resumeBand: document.getElementById('scz-resume-band'),
      resumeText: document.getElementById('scz-resume-text'),
      resumeReset: document.getElementById('scz-resume-reset'),
      infoBlock: document.getElementById('scz-info-block'),
      stage: root.querySelector('.scz-stage'),
      body: root.querySelector('.scz-body'),
      cardLines: document.getElementById('scz-card-lines'),
      rows: [].slice.call(root.querySelectorAll('.scz-line-row')),
      inputs: [].slice.call(root.querySelectorAll('.scz-line-input')),
      highlights: [].slice.call(root.querySelectorAll('.scz-highlight')),
      meterLine: document.getElementById('scz-meter-line'),
      marks: document.getElementById('scz-marks'),
      spaceCue: document.getElementById('scz-space-cue'),
      steps: document.getElementById('scz-steps'),
      kbbar: document.getElementById('scz-kbbar'),
      kbbarLabel: document.getElementById('scz-kbbar-label'),
      kbbarSeg: document.getElementById('scz-kbbar-seg'),
      kbbarDone: document.getElementById('scz-kbbar-done'),
      qtyInput: document.getElementById('scz-qty-input'),
      qtyDec: document.getElementById('scz-qty-dec'),
      qtyInc: document.getElementById('scz-qty-inc'),
      card: document.getElementById('scz-card'),
      caption: document.getElementById('scz-caption'),
      swatches: document.getElementById('scz-swatches'),
      swatchLabel: document.getElementById('scz-swatch-label'),
      form: document.getElementById('scz-form'),
      formQty: document.getElementById('scz-form-qty'),
      modal: document.getElementById('scz-modal'),
      modalMeta: document.getElementById('scz-modal-meta'),
      modalEcho: document.getElementById('scz-modal-echo'),
      modalBack: document.getElementById('scz-modal-back'),
      modalConfirm: document.getElementById('scz-modal-confirm'),
      infoModal: document.getElementById('scz-info-modal'),
      infoSetup: document.getElementById('scz-info-setup'),
      infoTiers: document.getElementById('scz-info-tiers'),
      infoTotal: document.getElementById('scz-info-total'),
      infoClose: document.getElementById('scz-info-close'),
      // #223 "Need design assistance?" (present only while custom.set_for_you is open)
      assist: document.getElementById('scz-assist'),
      linkRow: root.querySelector('.scz-linkrow'),
      // Proof draft (2026-09-03)
      proofLink: document.getElementById('scz-proof-link'),
      proofModal: document.getElementById('scz-proof-modal'),
      proofX: document.getElementById('scz-proof-x'),
      proofAsk: document.getElementById('scz-proof-ask'),
      proofDone: document.getElementById('scz-proof-done'),
      proofForm: document.getElementById('scz-proof-form'),
      proofCopy: document.getElementById('scz-proof-copy'),
      proofEmail: document.getElementById('scz-proof-email'),
      proofChange: document.getElementById('scz-proof-change'),
      proofConsent: document.getElementById('scz-proof-consent'),
      proofStatus: document.getElementById('scz-proof-status'),
      proofSkip: document.getElementById('scz-proof-skip'),
      proofSend: document.getElementById('scz-proof-send'),
      proofDoneCopy: document.getElementById('scz-proof-done-copy'),
      proofPreview: document.getElementById('scz-proof-preview'),
      proofResumeLink: document.getElementById('scz-proof-resume-link'),
      proofDoneClose: document.getElementById('scz-proof-done-close'),
    };

    // The composing core (#139). Same rules as the server's re-check; without
    // it nothing here can measure a line, so the page says so and stops.
    var Core = window.SaturnComposerCore;
    if (!Core) {
      warn('SaturnComposerCore missing — saturn-composer-core.js must load before this file');
      var cap0 = document.getElementById('scz-caption');
      if (cap0) cap0.textContent = 'The composer could not load — please refresh.';
      return;
    }
    var format = root.getAttribute('data-format') === 'landscape' ? 'landscape' : 'portrait';
    var catalog = null;
    var limits = null;            // Core.composeLimits for the current face + format
    var lastValidation = null;    // the core's ValidationResult from the last validateAll()
    var hasTyped = false;         // any line non-empty — wakes the chips, hides the cue
    var pricingConfig = null; // full door-keyed config; door D is ours
    var menuFaces = [];
    var maxLines = 6;
    var currentFace = null, currentSize = null, currentCfg = null;
    var currentPlacement = 'twoThirds';
    var inkHex = '#A6192E';
    var minQty = 50;
    var lastValidity = { ok: false, reason: 'incomplete' };
    var lastMeasures = [];
    var proofTimer = null, proofSeq = 0;
    var draftTimer = null, restoring = false;
    // #81 abandon signal: a session that reached a VALID composition and left
    // without ever pressing Add to cart. `proofsMinted` counts successful mints
    // (not attempts — proofSeq increments per attempt, including ones that
    // fail or get superseded), so the two are not interchangeable.
    var atcClicked = false, proofsMinted = 0;

    // ── #81 beacon telemetry (pseudonymous, invisible — quiet-room compatible)
    // Small behavioral events ride to the Planner so Door D's learning loop
    // can see where composing sessions stall. NO message text is ever sent —
    // labels/flags only (the endpoint enforces the same rule server-side).
    // Pseudonymous, not anonymous: the visitor id also rides proof payloads,
    // which do carry composition text — that join is the learning loop.
    //
    // Consent: the persistent visitor id, stored attribution, and event
    // delivery all follow Shopify's consent signal (the same
    // consent-tracking-api the theme's cookie banner drives). Until the API
    // answers, events buffer in memory; a "no" drops them and removes the
    // stored identifiers; where no consent is required the API answers yes at
    // load. Ids stay ephemeral (per-pageload) unless tracking is permitted.
    // Telemetry must never break composing: every surface is try/caught and
    // sendBeacon is fire-and-forget.
    var beacon = makeBeacon(root, function () { return catalog ? String(catalog.catalogVersion) : null; });

    // #81 funnel: the step between landing on the page and committing to words.
    // `pdp_loaded` fires as soon as the catalog resolves — it is a PAGEVIEW, not
    // intent — so without this the only signal above `first_keystroke` was "the
    // page rendered", and the open->type drop read as a UX cliff when much of it
    // is just people who never engaged at all.
    //
    // Fires once per page load on the first deliberate control change (face /
    // size / placement) OR the first keystroke, so the funnel stays monotonic:
    // pdp_loaded >= first_interaction >= first_keystroke. Every call site sits
    // inside a real DOM listener; setFace/setSize during draft restore assign
    // .value directly and dispatch nothing, so a restored draft alone is never
    // counted as interaction.
    function markInteraction() { beacon.emit('first_interaction', null, { once: true }); }

    // #81: device shape. The Aug-9 read of the compose→cart gap could not tell
    // whether the composers who never pressed Add to cart were on phones,
    // because the beacon carried no device signal at all. Two coarse labels
    // answer it without fingerprinting: the layout breakpoint the session
    // actually rendered at (the composer's own 900px switch), and whether the
    // pointer is touch. Bucketed, never the raw viewport — a precise
    // width×height pair is an identifying signal and we have no use for one.

    var pricingEl = document.getElementById('saturn-pricing-data');
    try { pricingConfig = pricingEl ? JSON.parse(pricingEl.textContent) : null; } catch (e) { warn('pricing island parse: ' + e); }
    var doorCfg = pricingConfig && pricingConfig.doors && pricingConfig.doors.D;
    if (doorCfg && doorCfg.minQty) minQty = doorCfg.minQty;
    els.qtyInput.min = minQty;
    els.qtyInput.value = minQty;

    // ── Catalog ─────────────────────────────────────────────────────────────
    fetch(root.getAttribute('data-catalog-url'))
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        catalog = Core.parseCatalog(data); // the same parse the server runs
        maxLines = Math.min(catalog.maxLines || 6, els.rows.length);
        els.rows.forEach(function (row, i) { row.hidden = i >= maxLines; });
        currentPlacement = (catalog.messagePlacement && catalog.messagePlacement.default) || 'twoThirds';
        inkHex = (catalog.ink && catalog.ink.hex) || inkHex;
        // A face reaches the menu only with a webFont, no exclusion flag, and
        // at least one size at/above the catalog's customer size floor —
        // sizes below minMenuPointSize never render (owner 2026-07-17: tiny
        // sizes are not a choice; Garamond Light, 8pt-only, drops out here).
        menuFaces = Core.menuFaces(catalog).sort();
        loadWebFonts(menuFaces.map(function (f) { return catalog.faces[f].webFont; }));
        renderFaceList();
        renderCaption();
        applyLayout();
        initSwatchMode();
        // URL param wins; otherwise a ONE-HOP relay context (design swap
        // mid-edit — written by "Choose a different design", consumed here)
        // keeps the edit session alive on the newly chosen design.
        var params = new URLSearchParams(location.search);
        var urlEditKey = params.get('edit-line');
        // Proof-draft resume link (2026-09-03): `?draft=<token>` brings a
        // server-held draft back on any device. A link click is deliberate,
        // so it wins over a local draft; an edit-line URL still wins over it.
        var draftToken = urlEditKey ? null : params.get('draft');
        // The relay is spent by THIS load whichever branch wins — a hop must
        // never outlive the page it was written for (see EDIT_CTX_KEY).
        var relayEditKey = takeEditCtx();
        if (urlEditKey || draftToken) relayEditKey = null;
        var editKey = urlEditKey || relayEditKey;
        if (editKey) { restoreFromCartLine(editKey, !!relayEditKey); }
        else if (draftToken) { restoreFromDraftToken(draftToken); }
        else { restoreDraft(); ensureDefaultFace(); }
        renderPrice();
        renderEmulator();
        updateExitState();
        // Land the caret on line 1 of a blank card (James, 2026-09-10) so the
        // "Start here" cue reads as "type here". Pointer-fine devices
        // only: on a phone an autofocus raises the keyboard over the card the
        // visitor has not seen yet.
        if (!editKey && !draftToken && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
          var first = activeInputs()[0];
          var blank = !activeInputs().some(function (i) { return i.value.trim(); });
          if (first && blank) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
        }
        var attrib = beacon.firstTouch();
        // Build stamp, then device keys, go FIRST: cleanMeta caps a meta object
        // at 8 keys and drops the overflow in iteration order, so the labels
        // this event exists to carry must not sit behind attribution's
        // variable-length tail (build + vw + ptr + format + edit + up to three
        // attribution keys = 8 exactly).
        beacon.emit(
          'pdp_loaded',
          Object.assign(buildMeta(root), deviceMeta(), { format: format, edit: !!editKey }, attrib),
          { once: true, now: true }
        );
        if (urlEditKey) beacon.emit('edit_from_cart', null, { once: true });
        if (relayEditKey) beacon.emit('design_swap_adopted', null, { once: true });
      })
      .catch(function (e) {
        warn('catalog load failed: ' + e.message);
        els.caption.textContent = 'Type menu unavailable — please refresh.';
      });

    // ── Web fonts (self-hosted @font-face + one Google Fonts request) ──────
    function loadWebFonts(mappings) {
      var assetBase = root.getAttribute('data-catalog-url').replace(/letterpress-catalog\.json.*$/, '');
      var faceRules = [], byFamily = {};
      mappings.forEach(function (m) {
        if (m.file) {
          faceRules.push('@font-face{font-family:"' + m.family + '";src:url("' + assetBase + m.file
            + '") format("woff2");font-weight:' + m.weight + ';font-style:' + m.style + ';font-display:swap}');
        } else {
          (byFamily[m.family] = byFamily[m.family] || []).push((m.style === 'italic' ? '1,' : '0,') + m.weight);
        }
      });
      if (faceRules.length) {
        var style = document.createElement('style');
        style.textContent = faceRules.join('\n');
        document.head.appendChild(style);
      }
      var families = Object.keys(byFamily).map(function (fam) {
        var tuples = byFamily[fam].filter(function (t, i, a) { return a.indexOf(t) === i; }).sort();
        return 'family=' + fam.replace(/ /g, '+') + ':ital,wght@' + tuples.join(';');
      });
      if (families.length) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?' + families.join('&') + '&display=swap';
        document.head.appendChild(link);
      }
    }

    function fontCss(face) {
      var wf = catalog.faces[face].webFont;
      return { family: '"' + wf.family + '", serif', weight: wf.weight, style: wf.style };
    }

    // Menu sizes come from the core (size-level menuExcluded + the menu floor).
    function menuSizes(face) { return Core.menuSizes(catalog, face); }

    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', function () {
        if (currentCfg) { validateAll(); syncAll(); }
      });
    }

    // ── Pickers + placement ────────────────────────────────────────────────
    // Choosing a typeface auto-selects its default size (middle of the menu)
    // so the line editor appears in one click — size stays freely changeable.
    // Typed lines PERSIST across a face change (James walkthrough 2026-07-24):
    // the message re-renders and re-validates in the new face — per-line
    // Ludlow inventory and the measure limit differ per face, so the issues
    // box and width meter speak for anything that no longer fits.
    // The typeface list: one button per menu face, rendered IN its face, with
    // the sizes on offer beside it. A list, not a <select>, so the choice can
    // be seen before it is made (Claude Design 1d / desktop panel).
    function renderFaceList() {
      els.font.innerHTML = '';
      menuFaces.forEach(function (face) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'scz-face'; b.setAttribute('role', 'radio');
        b.setAttribute('data-face', face); b.setAttribute('aria-checked', 'false');
        var css = fontCss(face);
        var name = document.createElement('span');
        name.className = 'scz-face-name'; name.textContent = face;
        name.style.fontFamily = css.family; name.style.fontWeight = css.weight; name.style.fontStyle = css.style;
        var sizes = document.createElement('span');
        sizes.className = 'scz-face-sizes';
        sizes.textContent = menuSizes(face).map(function (x) { return parseInt(x, 10); }).join(' · ') + 'pt';
        b.appendChild(name); b.appendChild(sizes);
        els.font.appendChild(b);
      });
    }
    function renderSizeSeg() {
      els.size.innerHTML = '';
      if (!currentFace) return;
      var sizes = menuSizes(currentFace);
      // Three or more sizes take the full panel width — all on one row, with
      // Placement dropped below (owner 2026-09-12); two share a row with it.
      var row = els.size.closest('.scz-style-row');
      if (row) row.classList.toggle('scz-style-row--stack', sizes.length > 2);
      sizes.forEach(function (sz) {
        var b = document.createElement('button');
        b.type = 'button'; b.setAttribute('role', 'radio'); b.setAttribute('data-size', sz);
        b.setAttribute('aria-checked', String(sz === currentSize));
        b.textContent = sz;
        els.size.appendChild(b);
      });
    }
    function markChecked(container, attr, value) {
      [].slice.call(container.querySelectorAll('button[' + attr + ']')).forEach(function (b) {
        b.setAttribute('aria-checked', String(b.getAttribute(attr) === value));
      });
    }
    els.font.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-face]');
      if (!btn) return;
      var face = btn.getAttribute('data-face');
      if (face === currentFace) return;
      markInteraction(); touchStyle(); beacon.emit('face_switch', { to: face });
      setFace(face);
      var sizes = menuSizes(face);
      if (sizes.length) setSize(sizes[Math.floor((sizes.length - 1) / 2)], true);
    });
    els.size.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-size]');
      if (!btn) return;
      var sz = btn.getAttribute('data-size');
      if (sz === currentSize) return;
      markInteraction(); touchStyle(); beacon.emit('size_switch', { to: sz });
      setSize(sz, true);
    });

    function setFace(face) {
      currentFace = face;
      currentSize = null; currentCfg = null;
      limits = face ? Core.composeLimits(catalog, face, format) : null;
      markChecked(els.font, 'data-face', face);
      renderSizeSeg();
      updateChips();
      syncAll();
    }

    function setSize(size, revalidate) {
      currentSize = size;
      currentCfg = (currentFace && size) ? catalog.faces[currentFace].sizes[size] : null;
      markChecked(els.size, 'data-size', size);
      updateChips();
      if (currentCfg) {
        var cap = Core.lineCharCap(currentCfg, limits);
        activeInputs().forEach(function (inp) { inp.maxLength = cap; });
        if (document.fonts && document.fonts.load) {
          document.fonts.load(measureFontSpec()).then(function () {
            if (currentCfg) { validateAll(); syncAll(); }
          });
        }
        if (revalidate) validateAll();
      }
      syncAll();
    }

    // Reflect a placement value into the buttons + hint (used by both the
    // click handler and draft restore, so the two never drift apart).
    function applyPlacementUI(placement) {
      currentPlacement = placement;
      els.placement.querySelectorAll('button').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-placement') === placement));
      });
      els.placementHint.textContent = placement === 'center'
        ? 'Centered sets the message in the middle — for cards where the printed message is the whole interior.'
        : 'Classic sits the message high, leaving room below for your own handwriting.';
      updateChips();
    }

    // Phone chips mirror the three style choices; tapping any opens the sheet.
    function updateChips() {
      if (!els.chipFace) return;
      els.chipFace.firstElementChild.textContent = currentFace || 'Typeface';
      els.chipSize.firstElementChild.textContent = currentSize || 'Size';
      els.chipPlacement.firstElementChild.textContent = currentPlacement === 'center' ? 'Centered' : 'Classic';
    }
    function openStyleSheet() {
      if (!els.styleSheet) return;
      els.styleSheet.classList.add('scz-open');
    }
    function closeStyleSheet() {
      if (!els.styleSheet) return;
      // Closing the sheet counts as having met the style step (#155).
      if (els.styleSheet.classList.contains('scz-open')) touchStyle();
      els.styleSheet.classList.remove('scz-open');
    }
    [els.chipFace, els.chipSize, els.chipPlacement].forEach(function (c) {
      if (c) c.addEventListener('click', function () { markInteraction(); openStyleSheet(); });
    });
    if (els.styleDone) els.styleDone.addEventListener('click', closeStyleSheet);
    if (els.styleSheet) els.styleSheet.addEventListener('click', function (e) { if (e.target === els.styleSheet) closeStyleSheet(); });

    els.placement.addEventListener('click', function (e) {
      var btn = e.target.closest('button[data-placement]');
      if (!btn) return;
      markInteraction();
      touchStyle(); beacon.emit('placement_switch', { to: btn.getAttribute('data-placement') });
      applyPlacementUI(btn.getAttribute('data-placement'));
      syncAll();
    });

    function activeInputs() { return els.inputs.slice(0, maxLines); }

    els.inputs.forEach(function (inp, idx) {
      inp.addEventListener('input', function () {
        markInteraction();
        beacon.emit('first_keystroke', { face: currentFace, size: currentSize }, { once: true });
        if (currentCfg && limits) {
          var cap = Core.lineCharCap(currentCfg, limits);
          if (inp.value.length > cap) inp.value = inp.value.slice(0, cap);
        }
        validateAll();
        syncAll();
      });
      // Nowrap lines can scroll horizontally past the measure limit; the
      // overlay must track the textarea's scroll or caret and text drift apart.
      inp.addEventListener('scroll', function () {
        els.highlights[idx].scrollLeft = inp.scrollLeft;
      });
      // Word-processor keys on a card of fixed lines (owner 2026-09-12):
      //  • Enter splits the line at the caret and pushes the lines below down
      //    (only while the last line is free — the card has maxLines lines, so
      //    with no room Enter just moves to the next line as before);
      //  • Backspace at the start of a line joins it onto the line above and
      //    pulls the lines below up (when the join fits the line's cap);
      //  • Up / Down move between lines, keeping the caret column.
      inp.addEventListener('keydown', function (e) {
        var inputs = activeInputs(), last = inputs.length - 1;
        var caret = typeof inp.selectionStart === 'number' ? inp.selectionStart : inp.value.length;
        var place = function (target, col) {
          if (!target) return;
          target.focus();
          try { var c = Math.min(col, target.value.length); target.setSelectionRange(c, c); } catch (err) { /* fine */ }
        };
        var changed = function () { markInteraction(); validateAll(); syncAll(); };
        if (e.key === 'Enter') {
          e.preventDefault();
          // #155: Return on an EMPTY line directly under a typed line opens a
          // space above it (large, or small when large will not fit) and the
          // caret stays — the line is not spent. The word-processor instinct
          // (Return twice for a blank line) keeps working.
          if (!inp.value.trim() && idx > 0 && typedAt(idx - 1)) {
            if (gaps[idx - 1] === 0 && !setGap(idx - 1, 2, 'return', idx)) setGap(idx - 1, 1, 'return', idx);
            return;
          }
          if (idx >= last) return;
          if (inputs[last].value === '') {
            // A row is born under idx: the gaps below it move down with their lines.
            gaps.splice(idx, 0, 0); gaps.length = GAP_SLOTS;
            for (var k = last; k > idx + 1; k--) inputs[k].value = inputs[k - 1].value;
            // The space at the break belongs to neither line — a metal line
            // has no use for a trailing space and the case counts every one.
            inputs[idx + 1].value = inp.value.slice(caret).replace(/^\s+/, '');
            inp.value = inp.value.slice(0, caret).replace(/\s+$/, '');
            changed();
            place(inputs[idx + 1], 0);
          } else {
            place(inputs[idx + 1], caret);
          }
        } else if (e.key === 'Backspace' && caret === 0 && inp.selectionEnd === 0 && idx > 0) {
          e.preventDefault();
          var prev = inputs[idx - 1];
          var cap = (currentCfg && limits) ? Core.lineCharCap(currentCfg, limits) : Infinity;
          if (prev.value.length + inp.value.length <= cap) {
            var col = prev.value.length;
            // The joined rows' own gap goes; the gaps below move up with their lines.
            gaps.splice(idx - 1, 1); gaps.push(0);
            prev.value = prev.value + inp.value;
            for (var m = idx; m < last; m++) inputs[m].value = inputs[m + 1].value;
            inputs[last].value = '';
            changed();
            place(prev, col);
          } else {
            place(prev, prev.value.length);
          }
        } else if (e.key === 'ArrowUp' && idx > 0) {
          e.preventDefault();
          place(inputs[idx - 1], caret);
        } else if (e.key === 'ArrowDown' && idx < last) {
          e.preventDefault();
          place(inputs[idx + 1], caret);
        }
      });
    });

    // Click anywhere on the card to compose: focus the first empty line
    // (or the last line once all are filled).
    els.card.addEventListener('click', function (e) {
      if (e.target.closest('.scz-line-input')) return;
      if (!currentCfg) return;
      var lines = activeInputs();
      var target = lines.filter(function (i) { return !i.value.trim(); })[0] || lines[lines.length - 1];
      if (target) target.focus();
    });

    // ── Validation: the core's rules (#139) ────────────────────────────────
    // Card measure on the whole line, the bed per cast slug (two slugs, spliced
    // at a word space), the type case per slug. Identical arithmetic to the
    // server's re-check because it IS the same code.
    var measureCtx = document.createElement('canvas').getContext('2d');
    var measurer = Core.canvasMeasurer(measureCtx);

    function measureFontSpec() {
      return Core.measureFontSpec(catalog.faces[currentFace].webFont, currentSize);
    }

    // ── Line spaces (#155, owner 2026-09-17) ───────────────────────────────
    // A space is leads between two lines of type — never one of the
    // lines of type. State is `gaps`: gaps[i] sits between input i and input i+1;
    // 0 none · 1 small (the core's half space) · 2 large (full). The customer
    // never sees "half / full / leading". The core's shape is a blank entry in
    // lines[] (+ halfSpaces), so compose() is the one translation point, and
    // `map` carries each composed entry back to its input (−1 = a space) —
    // validation reports and line numbers are remapped through it.
    // One slot per gap between RENDERED rows (the section renders 6 since #206); a slot
    // past catalog.maxLines stays 0 because typedAt() is false there.
    var GAP_SLOTS = Math.max(0, els.rows.length - 1);
    function noGaps() { var a = []; for (var i = 0; i < GAP_SLOTS; i++) a.push(0); return a; }
    var gaps = noGaps();
    var SPACE_CUE_KEY = 'sp-lp-space-cue-v1';
    var SEQUENCING = root.getAttribute('data-sequencing') === 'on';
    var styleTouched = false;      // the phone dock's soft sequence (Start typing → Style it → Review proof)
    var spaceCueSeen = false;
    try { spaceCueSeen = localStorage.getItem(SPACE_CUE_KEY) === '1'; } catch (e) { /* storage unavailable */ }
    var lastMap = [];

    function typedAt(i) { var inp = els.inputs[i]; return !!(inp && i < maxLines && inp.value.trim()); }
    function typedCount() { return activeInputs().filter(function (i) { return i.value.trim(); }).length; }

    // `assume`: an input index to treat as typed (the Return key sets a space
    // above an empty line before its text exists).
    function compose(g, assume) {
      g = g || gaps;
      var vals = activeInputs().map(function (i) { return i.value; });
      if (typeof assume === 'number' && vals[assume] !== undefined && !vals[assume].trim()) vals[assume] = 'x';
      var lines = [], half = [], map = [], prev = -1, pending = 0, last = -1;
      vals.forEach(function (v, i) { if (v.trim()) last = i; });
      for (var i = 0; i <= last; i++) {
        if (!vals[i].trim()) {
          // Before the message: a leading blank still spends a line (unchanged).
          // Inside it: an empty line between two typed lines IS a large space —
          // normalizeRows() makes the inputs say so once the caret leaves.
          if (prev < 0) { lines.push(''); map.push(i); } else pending = 2;
          continue;
        }
        if (prev >= 0) {
          var gv = pending;
          for (var k = prev; k < i; k++) gv = Math.max(gv, g[k] || 0);
          if (gv) { if (gv === 1) half.push(lines.length); lines.push(''); map.push(-1); }
        }
        lines.push(vals[i]); map.push(i); prev = i; pending = 0;
      }
      return { lines: lines, halfSpaces: half, map: map };
    }

    function compositionWith(g, assume) {
      var c = compose(g, assume);
      var out = { face: currentFace, size: currentSize, placement: currentPlacement, format: format, lines: c.lines };
      if (c.halfSpaces.length) out.halfSpaces = c.halfSpaces;
      lastMap = c.map;
      return out;
    }
    function composition() { return compositionWith(gaps); }

    // The fit rule: type keeps a 1″ bottom margin (core: typeFit). A space that
    // will not fit is simply not offered.
    function fitsWith(g, assume) {
      if (!catalog || !currentCfg) return false;
      var map = lastMap, f;
      try { f = Core.fitOf(compositionWith(g, assume), catalog); } finally { lastMap = map; }
      return !f || f.fits;
    }
    function canSet(i, state, assume) {
      if (!typedAt(i) || !(typedAt(i + 1) || assume === i + 1)) return false;
      if (!state) return true;
      var g = gaps.slice(); g[i] = state;
      return fitsWith(g, assume);
    }
    // Only gaps between two typed lines are real; the rest is remembered
    // intent (text deleted for a moment) and never leaves the page.
    function appliedGaps() { return gaps.map(function (g, i) { return typedAt(i) && typedAt(i + 1) ? g : 0; }); }
    function dismissSpaceCue() {
      if (spaceCueSeen) return;
      spaceCueSeen = true;
      try { localStorage.setItem(SPACE_CUE_KEY, '1'); } catch (e) { /* fine */ }
    }
    function setGap(i, state, via, assume) {
      if (i < 0 || i >= GAP_SLOTS || !canSet(i, state, assume)) return false;
      var was = gaps[i];
      if (was === state) return true;
      gaps[i] = state;
      markInteraction();
      dismissSpaceCue();
      if (state) beacon.emit('space_set', { gap: i + 1, state: state === 1 ? 'small' : 'large', via: via });
      else beacon.emit('space_removed', { gap: i + 1, via: via });
      validateAll();
      syncAll();
      return true;
    }
    // Tap steps none → small → large → none, skipping a state that will not fit.
    function nextGapState(i) {
      var order = [1, 2, 0], cur = gaps[i];
      for (var k = 0; k < 3; k++) {
        var cand = order[(order.indexOf(cur) + 1 + k) % 3];
        if (cand === 0 || canSet(i, cand)) return cand;
      }
      return 0;
    }
    // An empty line between two typed lines becomes a large space and hands the
    // line back (a smaller one when large will not fit). Runs when the caret
    // leaves the card, before Review, and on every restore — so a saved draft
    // from before #155 converts the same way. Returns how many it converted.
    function normalizeRows() {
      var inputs = activeInputs(), n = inputs.length, converted = 0;
      for (var i = 1; i < n - 1; i++) {
        if (inputs[i].value.trim() || !inputs[i - 1].value.trim()) continue;
        var below = false;
        for (var k = i + 1; k < n; k++) if (inputs[k].value.trim()) below = true;
        if (!below) break;
        for (var m = i; m < n - 1; m++) inputs[m].value = inputs[m + 1].value;
        inputs[n - 1].value = '';
        var want = Math.max(gaps[i - 1], gaps[i], 2);
        gaps.splice(i, 1); gaps.push(0); gaps[i - 1] = want;
        converted++; i--;
      }
      gaps = appliedGaps();
      if (converted) {
        // Converted spaces yield to the fit rule, largest first from the bottom.
        var guard = 0;
        while (!fitsWith(gaps) && guard++ < 2 * GAP_SLOTS + 2) {
          var j = -1;
          for (var q = GAP_SLOTS - 1; q >= 0; q--) if (gaps[q] === 2) { j = q; break; }
          if (j < 0) for (var q2 = GAP_SLOTS - 1; q2 >= 0; q2--) if (gaps[q2] === 1) { j = q2; break; }
          if (j < 0) break;
          gaps[j]--;
        }
      }
      return converted;
    }
    function readGaps(v) {
      var a = typeof v === 'string' ? v.split(',') : (Array.isArray(v) ? v : []);
      return noGaps().map(function (_z, i) { var g = parseInt(a[i], 10); return g === 1 || g === 2 ? g : 0; });
    }

    // Spaces stay literal (white-space: pre-wrap renders them) so the overlay
    // wraps identically to the textarea — nbsp broke wrapping parity and
    // caused the owner-reported doubled-letters drift.
    function escapeChar(ch) {
      return ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch;
    }

    function inchesLabel(pica) { return Core.inchesLabel(pica); }

    // The first thing to fix, in the core's customer wording — rendered under
    // the card (the status box left with the rail). Empty when nothing is wrong.
    var firstIssueText = '';
    var firstIssueLine = -1;

    function validateAll() {
      if (!currentCfg || !limits) { lastValidity = { ok: false, reason: 'incomplete' }; lastValidation = null; syncAtcState(); return; }
      var r = Core.validateComposition(composition(), catalog, measurer, limits);
      lastValidation = r;
      // The report is indexed by COMPOSED entry (spaces included); the page is
      // indexed by input. `at[input]` is the entry that input became.
      var at = {};
      lastMap.forEach(function (inputIdx, entry) { if (inputIdx >= 0) at[inputIdx] = entry; });
      var inputLine = function (lineNum) { var ix = lastMap[lineNum - 1]; return (typeof ix === 'number' && ix >= 0) ? ix + 1 : lineNum; };
      var renumber = function (issue) { var o = {}; for (var key in issue) o[key] = issue[key]; o.lineNum = inputLine(issue.lineNum); return o; };
      lastMeasures = activeInputs().map(function (inp, idx) { return at[idx] === undefined ? 0 : (r.measures[at[idx]] || 0); });
      // Paint the overlays: the offending characters (case or measure) in red.
      activeInputs().forEach(function (inp, idx) {
        var text = inp.value, rep = at[idx] === undefined ? null : r.lines[at[idx]];
        var html = '';
        for (var i = 0; i < text.length; i++) {
          var esc = escapeChar(text[i]);
          html += (rep && rep.badIndexes.has(i)) ? '<span class="scz-over">' + esc + '</span>' : esc;
        }
        els.highlights[idx].innerHTML = html;
      });
      // Pain-point signals (#81): first time each warning class fires this
      // session — restore paths excluded so only live typing counts.
      if (!restoring) {
        if (r.measureIssues.length) beacon.emit('width_warning', { face: currentFace, size: currentSize }, { once: true });
        if (r.inventoryIssues.length) beacon.emit('inventory_warning', { face: currentFace, size: currentSize }, { once: true });
      }
      firstIssueText = ''; firstIssueLine = -1;
      if (r.measureIssues.length || r.inventoryIssues.length) {
        // The lowest line with a problem speaks first; measure before case on the same line.
        var m = r.measureIssues[0] && renumber(r.measureIssues[0]), v = r.inventoryIssues[0] && renumber(r.inventoryIssues[0]);
        if (m && (!v || m.lineNum <= v.lineNum)) { firstIssueText = Core.describeMeasureIssue(m); firstIssueLine = m.lineNum - 1; }
        else if (v) { firstIssueText = Core.describeInventoryIssue(v); firstIssueLine = v.lineNum - 1; }
      } else if (r.reason === 'too_tall' && r.fit) {
        // A space that would not fit is never offered in the first place; this
        // is reached by a size or placement change after spaces were set, or
        // (since the press's 2.0 row, 2026-09-21) by a fourth line of 36pt on
        // a landscape card.
        firstIssueText = Core.describeFit(r.fit);
        activeInputs().forEach(function (inp, idx) { if (inp.value.trim()) firstIssueLine = idx; });
        if (!restoring) beacon.emit('too_tall_shown', { face: currentFace, size: currentSize }, { once: true });
      }
      if (els.cardLines) els.cardLines.classList.toggle('scz-too-tall', r.reason === 'too_tall');
      lastValidity = { ok: r.ok, reason: r.reason };
      hasTyped = typedCount() > 0;
      root.classList.toggle('scz-typed', hasTyped);
      renderCaption();
      syncAtcState();
      updateMeter();
    }

    // ── Add-to-cart gate (owner, 2026-08-21, from the #97 read) ───────────
    // Every customer `atc_blocked` event on record is `reason=incomplete` from a
    // visitor with ZERO proofs: they landed, saw the design, saw the button and
    // pressed it without choosing a typeface or typing a character. The composer
    // is a form, and its primary control was live before the form had anything
    // in it. This makes the button LOOK unavailable until the composition is
    // valid, so the order of operations is communicated before the click instead
    // of only after it.
    //
    // NOT the `disabled` attribute, deliberately, and this is the whole design:
    //   * A disabled button fires NO click event, so `atc_blocked` would stop
    //     firing — and that beacon is the ONLY positive trace this population
    //     leaves. `compose_abandoned` requires lastValidity.ok, so a
    //     never-composed visitor emits nothing else. Disabling would have
    //     removed the instrument in the same change that acted on its findings,
    //     and merged these visitors into "never interacted".
    //   * A disabled button also cannot explain itself and is not focusable.
    //     Pressing it today produces "Compose Your Message First" plus a bullet,
    //     scrolled into view — real guidance, and exactly what someone who does
    //     not know what to do next needs. `aria-disabled` keeps that.
    // So the click handler is untouched: a gated press still teaches, still
    // emits, and the metric stays comparable across this change.
    // The ONE primary action (#138). Phone: "Start typing" until the message is
    // valid, then "Review proof". Desktop: always "Review proof", quiet until
    // valid. Class-driven — never `disabled`, never aria-disabled (see above).
    // #155 soft sequence (phone, guidance never a gate): Start typing → Style
    // it → Review proof. The step is DERIVED: 1 = nothing typed, 2 = typed but
    // no style control touched, 3 = otherwise. The step words above the button
    // are links, so Review is always one tap away. Desktop has the panel open
    // beside the card, so it only gains "Start typing" on a blank card.
    function dockStep() {
      if (!hasTyped) return 1;
      return (SEQUENCING && isPhone() && !styleTouched) ? 2 : 3;
    }
    function syncAtcState() {
      if (!els.atc) return;
      var ready = !!lastValidity.ok, step = dockStep();
      els.atc.classList.toggle('scz-atc-gated', step === 1 || (step === 3 && !ready));
      els.atc.textContent = step === 1 ? 'Start typing' : step === 2 ? 'Style it' : 'Review proof';
      if (step === 2 || ready) els.atc.removeAttribute('title');
      else els.atc.setAttribute('title', step === 1 ? (isPhone() ? 'Tap to start your message' : 'Click to start your message') : 'Compose your message to continue');
      if (els.steps) {
        els.steps.hidden = !(SEQUENCING && isPhone() && hasTyped);
        [].forEach.call(els.steps.querySelectorAll('button'), function (b) {
          b.classList.toggle('scz-step-on', parseInt(b.getAttribute('data-step'), 10) === step);
        });
      }
    }
    function touchStyle() { if (!styleTouched) { styleTouched = true; syncAtcState(); } }

    // Focused-line width meter, shown under the card (the per-line rail meters
    // left with the rail editor). The status box still lists every issue.
    function updateMeter() {
      if (!els.meterLine) return;
      if (firstIssueText) {
        els.meterLine.textContent = firstIssueText;
        els.meterLine.classList.add('scz-issue');
        return;
      }
      els.meterLine.classList.remove('scz-issue');
      var idx = els.inputs.indexOf(document.activeElement);
      if (!currentCfg || !limits || idx === -1 || idx >= maxLines || !els.inputs[idx].value) {
        els.meterLine.textContent = '';
        return;
      }
      var w = lastMeasures[idx] || 0;
      // Y = the CARD's measure (4″ / 5.5″) — what a line may run once the
      // press splices two slugs. Slugs are the pressman's concern, not the
      // customer's; the job sheet carries them.
      els.meterLine.textContent = 'Line ' + (idx + 1) + ' — ' + inchesLabel(w) + '″ of ' + inchesLabel(limits.limitPica) + '″';
    }
    root.addEventListener('focusin', updateMeter);
    root.addEventListener('focusout', function () { setTimeout(updateMeter, 0); });

    // ── Emulator = editor (owner 2026-07-18, Papier reference) ─────────────
    // The card box is true-aspect; scale maps rendered card width to physical
    // card width. The line inputs live ON the card at the customer-chosen
    // placement, styled in the chosen face at real proportional size in the
    // shop's red — typing happens directly in the preview.
    function renderEmulator() {
      if (!catalog || !els.cardLines) return;
      var dims = (catalog.cardDimensionsIn && catalog.cardDimensionsIn[format]) || (format === 'landscape' ? [7, 5] : [5, 7]);
      var opt = Core.placementOption(catalog, currentPlacement);
      els.cardLines.style.top = (opt.verticalCenterRatio * 100) + '%';
      // #139: the band spans the CARD's line measure (bandGeometry in the core —
      // the same width the admin composer and the job sheet draw).
      var bandPica = limits ? limits.limitPica : (catalog.maxLineMeasurePica || 22.5);
      els.cardLines.style.width = (Core.bandGeometry(dims, bandPica, opt).widthRatio * 100) + '%';
      els.cardLines.hidden = !currentCfg;
      // "Start here" cue above line 1 until the first keystroke; row 1 keeps a
      // dashed outline meanwhile so the place to type is visible unfocused.
      if (els.startCue) els.startCue.hidden = !currentCfg || hasTyped;
      if (els.rows[0]) els.rows[0].classList.toggle('scz-cue-row', !!currentCfg && !hasTyped);
      if (!currentCfg) return;
      var css = fontCss(currentFace);
      var scale = els.card.clientWidth ? els.card.clientWidth / (dims[0] * 96) : 1;
      var pt = parseInt(currentSize, 10);
      var px = pt * PX_PER_PT * scale;
      var rowH = Math.ceil(px * Core.lineHeight(pt));
      // Anchor semantics from the catalog (owner 2026-07-18, round 2):
      // 'baseline' seats the FIRST LINE's bottom ON the placement line —
      // lines 2+ flow below it. (Top-anchoring pushed the whole block under
      // the line; block-centering rode too high on landscape.) 'center' seats
      // the four-row box on the line and fills it from the top (owner
      // 2026-09-14) — the box capped at 18pt type so it never climbs into the
      // Classic placement (#153), and grown to fit a block taller than itself
      // (owner + James 2026-09-15). The core states the rule; the old -50%
      // centred this element, which is the same box only at 18pt and below.
      var lines = activeInputs().map(function (i) { return i.value; });
      var drawn = 0; lines.forEach(function (l, i) { if (l.trim()) drawn = i + 1; });
      // #155: a space is a margin above the lower row — the core's spaceEm ×
      // the type size (whole leads, #179), the same leads the proof and the job
      // sheet draw. Shown once both lines have type, or while the caret sits
      // in the (still empty) lower line so a Return-made space is visible.
      var focusIdx = els.inputs.indexOf(document.activeElement), gapPx = 0;
      activeInputs().forEach(function (inp, i) {
        var g = i > 0 ? gaps[i - 1] : 0;
        var on = g && typedAt(i - 1) && (typedAt(i) || focusIdx === i);
        var mt = on ? px * Core.spaceEm(pt, g === 1) : 0;
        els.rows[i].style.marginTop = mt ? mt + 'px' : '';
        if (i < drawn) gapPx += mt;
      });
      els.cardLines.style.transform = Core.emulatorTransform(opt.anchor, rowH, PX_PER_PT * scale, rowH * drawn + gapPx, Core.CENTER_BOX_ROWS);
      activeInputs().forEach(function (inp, i) {
        [inp, els.highlights[i]].forEach(function (el) {
          el.style.fontFamily = css.family;
          el.style.fontWeight = css.weight;
          el.style.fontStyle = css.style;
          el.style.fontSize = px + 'px';
          el.style.lineHeight = String(Core.lineHeight(pt));
        });
        inp.style.height = rowH + 'px';
        inp.style.caretColor = inkHex;
        els.highlights[i].style.color = inkHex;
        // No placeholder on any line (owner 2026-09-16 — see SAMPLE_MESSAGE's
        // former home near the top); a blank card is blank, empty lines just
        // take focus on click.
        inp.placeholder = '';
      });
      renderMarks();
    }
    window.addEventListener('resize', function () { renderEmulator(); });

    // ── #155 the on-card marks ─────────────────────────────────────────────
    // One pill per gap, in the card's margin, centred on the gap; a dotted
    // hairline is the lead once a space is set. Built once, then only moved
    // and relabelled (a rebuild per keystroke would drop hover and focus).
    // Composer chrome only: the proof is drawn on a canvas and never has them.
    var markEls = [], leadEls = [];
    var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>';
    function buildMarks() {
      if (!els.marks || markEls.length) return;
      for (var i = 0; i < GAP_SLOTS; i++) {
        var lead = document.createElement('div'); lead.className = 'scz-lead'; lead.hidden = true;
        var b = document.createElement('button'); b.type = 'button'; b.className = 'scz-mark'; b.hidden = true;
        b.setAttribute('data-gap', String(i));
        b.innerHTML = ARROW + '<span></span>';
        els.marks.appendChild(lead); els.marks.appendChild(b);
        leadEls.push(lead); markEls.push(b);
      }
      // Keep the caret (and a phone's keyboard) where it is while a mark is pressed.
      els.marks.addEventListener('mousedown', function (e) { if (e.target.closest('.scz-mark')) e.preventDefault(); });
      els.marks.addEventListener('click', function (e) {
        var btn = e.target.closest('.scz-mark'); if (!btn) return;
        e.stopPropagation(); // the card's own click handler focuses a line
        var i = parseInt(btn.getAttribute('data-gap'), 10);
        setGap(i, nextGapState(i), 'mark');
      });
    }
    function renderMarks() {
      if (!els.marks) return;
      buildMarks();
      var ready = !!currentCfg && !els.cardLines.hidden;
      var cardBox = els.card.getBoundingClientRect(), phone = isPhone(), anyShown = false;
      var verb = phone ? 'Tap' : 'Click';
      for (var i = 0; i < GAP_SLOTS; i++) {
        var b = markEls[i], lead = leadEls[i], g = gaps[i];
        var between = ready && typedAt(i) && typedAt(i + 1);
        var show = between && (g > 0 || canSet(i, 1));
        b.hidden = !show; lead.hidden = !(between && g > 0);
        if (!show) continue;
        anyShown = true;
        var above = els.rows[i].getBoundingClientRect();
        // The gap's centre, computed (not read) below the upper row: the lower row animates into place.
        var mt = parseFloat(els.rows[i + 1].style.marginTop) || 0;
        var y = above.bottom - cardBox.top + mt / 2;
        b.style.top = y + 'px'; lead.style.top = y + 'px';
        b.classList.toggle('scz-mark-set', g > 0);
        // Phone: alternate margins so adjacent pills never collide at 18pt.
        b.classList.toggle('scz-mark-left', phone && i === 1);
        b.lastElementChild.textContent = g === 0 ? 'Add space' : g === 1 ? 'Small space' : 'Large space';
        var next = nextGapState(i);
        b.title = g === 0 ? '' : next === 2 ? verb + ' for a larger space' : verb + ' to remove the space';
        b.setAttribute('aria-label', 'Between line ' + (i + 1) + ' and line ' + (i + 2) + ': '
          + (g === 0 ? 'no space. Add a small space' : g === 1 ? 'small space. ' + (next === 2 ? 'Make it large' : 'Remove it') : 'large space. Remove it'));
      }
      var focused = els.inputs.indexOf(document.activeElement) !== -1;
      els.marks.classList.toggle('scz-marks-quiet', !phone && !focused && gaps.some(function (g) { return g > 0; }));
      // The one cue: two typed lines, no space yet, a mark on offer, never touched.
      if (els.spaceCue) {
        var cue = anyShown && !spaceCueSeen && typedCount() >= 2 && !gaps.some(function (g) { return g > 0; }) && lastValidity.reason !== 'too_tall';
        els.spaceCue.hidden = !cue;
        if (cue) {
          els.spaceCue.textContent = verb + ' Add space beside a line to open room above it';
          if (!restoring) beacon.emit('space_cue_shown', null, { once: true });
        }
      }
      renderKbBar();
    }
    if (els.cardLines) els.cardLines.addEventListener('transitionend', function (e) { if (e.propertyName === 'margin-top') renderMarks(); });

    // ── #155 phone, keyboard up ────────────────────────────────────────────
    // The dock steps aside while a line has the caret; a strip rides the
    // keyboard's top edge with the space ABOVE the focused line. Pressing it
    // must not take the caret (the keyboard would drop), hence mousedown.
    var kbOn = false;
    function placeKbBar() {
      if (!kbOn || !els.kbbar) return;
      var vv = window.visualViewport;
      els.kbbar.style.bottom = vv ? Math.max(0, window.innerHeight - vv.height - vv.offsetTop) + 'px' : '0px';
    }
    function renderKbBar() {
      if (!els.kbbar) return;
      var idx = els.inputs.indexOf(document.activeElement);
      var on = isPhone() && idx !== -1 && idx < maxLines && !!currentCfg;
      if (on !== kbOn) { kbOn = on; root.classList.toggle('scz-kb', on); els.kbbar.hidden = !on; }
      if (!on) return;
      var both = idx > 0 && typedAt(idx) && typedAt(idx - 1);
      var offer = both && (gaps[idx - 1] > 0 || canSet(idx - 1, 1));
      els.kbbarSeg.hidden = !offer;
      els.kbbarLabel.textContent = offer ? 'Room above this line' : 'Line ' + (idx + 1) + ' of ' + maxLines;
      if (offer) {
        [].forEach.call(els.kbbarSeg.querySelectorAll('button'), function (b) {
          var st = parseInt(b.getAttribute('data-gap'), 10);
          b.setAttribute('aria-pressed', String(gaps[idx - 1] === st));
          b.hidden = st > 0 && gaps[idx - 1] !== st && !canSet(idx - 1, st);
        });
      }
      placeKbBar();
    }
    if (els.kbbar) {
      els.kbbar.addEventListener('mousedown', function (e) { e.preventDefault(); });
      els.kbbarSeg.addEventListener('click', function (e) {
        var b = e.target.closest('button[data-gap]'), idx = els.inputs.indexOf(document.activeElement);
        if (!b || idx < 1) return;
        setGap(idx - 1, parseInt(b.getAttribute('data-gap'), 10), 'bar');
      });
      els.kbbarDone.addEventListener('click', function () { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', placeKbBar);
        window.visualViewport.addEventListener('scroll', placeKbBar);
      }
    }
    // The caret entering or leaving the card: marks wake or go quiet, the bar
    // follows, and once it has LEFT the lines an empty middle line becomes a space.
    root.addEventListener('focusin', function (e) { if (e.target.closest && e.target.closest('.scz-line-input')) renderEmulator(); });
    root.addEventListener('focusout', function (e) {
      if (!(e.target.closest && e.target.closest('.scz-line-input'))) return;
      setTimeout(function () {
        if (els.inputs.indexOf(document.activeElement) !== -1) { renderEmulator(); return; }
        var before = gaps.join(',');
        if (normalizeRows() || before !== gaps.join(',')) { validateAll(); syncAll(); } else renderEmulator();
      }, 0);
    });
    if (els.steps) els.steps.addEventListener('click', function (e) {
      var b = e.target.closest('button[data-step]'); if (!b) return;
      var st = parseInt(b.getAttribute('data-step'), 10);
      markInteraction();
      if (st === 1) { var lastTyped = 0; activeInputs().forEach(function (inp, i) { if (inp.value.trim()) lastTyped = i; }); focusLine(lastTyped); }
      else if (st === 2) openStyleSheet();
      else { touchStyle(); els.atc.click(); }
    });

    // ── One DOM, two arrangements (#138) ───────────────────────────────────
    // The stepper, the primary button, the style panel, Save & exit, Email me
    // a proof and the draft state each exist ONCE and are docked into the
    // phone or desktop slots on the 900px query. appendChild keeps ids,
    // listeners and state; only the parent changes.
    var phoneQuery = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    // Narrow desktop: the bar cannot hold four actions beside a centred stepper.
    var narrowQuery = window.matchMedia ? window.matchMedia('(min-width: 901px) and (max-width: 1200px)') : null;
    function isPhone() { return !!(phoneQuery && phoneQuery.matches); }
    function isNarrow() { return !!(narrowQuery && narrowQuery.matches); }
    function dock(el, target) { if (el && target && el.parentNode !== target) target.appendChild(el); }
    function applyLayout() {
      var phone = isPhone();
      if (phone) {
        dock(els.headerQty, els.dockQty);
        dock(els.atc, els.dockAction);
        dock(els.assist, els.dockAction);   // full-width link under the dock row
        dock(els.stylePanel, els.styleDock);
        dock(els.draftState, els.meterSlot);
        dock(els.proofLink, els.cardLinks);
        dock(document.getElementById('scz-exit'), els.cardLinks);
        dock(els.infoBlock, els.body); // after the chips
      } else {
        closeStyleSheet();
        if (els.barMid && els.headerQty) els.barMid.insertBefore(els.headerQty, els.barMid.firstChild);
        if (isNarrow()) {
          // 901–1200px: state beside the meter, text links under the card, the bar keeps one button.
          dock(els.draftState, els.meterSlot);
          dock(els.proofLink, els.cardLinks);
          dock(document.getElementById('scz-exit'), els.cardLinks);
        } else {
          // Bar actions read left→right: state · proof link · Save & exit · Review proof.
          dock(els.draftState, els.barActions);
          dock(els.proofLink, els.barActions);
          dock(document.getElementById('scz-exit'), els.barActions);
        }
        dock(els.atc, els.barActions);
        // #223: right after the primary on a full bar; the narrow bar keeps one
        // button, so there it is a link in the row under the card.
        dock(els.assist, isNarrow() ? els.linkRow : els.barActions);
        dock(els.stylePanel, els.canvasCol);
        dock(els.infoBlock, els.stage); // under the link row, in the card's column
      }
      var swapLong = root.querySelector('.scz-swap-long'), swapShort = root.querySelector('.scz-swap-short');
      if (swapLong && swapShort) { swapLong.hidden = phone; swapShort.hidden = !phone; }
      if (els.startCueText) els.startCueText.textContent = phone ? 'Start here — tap to type your first line' : 'Start here — type your first line';
      renderCaption();
      syncAtcState();
      renderPrice();
      renderEmulator();
    }
    [phoneQuery, narrowQuery].forEach(function (q) {
      if (!q) return;
      var onLayout = function () { applyLayout(); };
      if (q.addEventListener) q.addEventListener('change', onLayout);
      else if (q.addListener) q.addListener(onLayout);
    });
    // Caption under the card: lines + the Return rule, then the card itself.
    // Every number comes from the catalog (dimensions, ink, line count).
    function renderCaption() {
      if (!els.caption || !catalog) return;
      var dims = Core.cardDims(catalog, format);
      var inkName = (catalog.ink && catalog.ink.name) ? ' (' + catalog.ink.name + ')' : '';
      // #155: once two lines exist the counter says what a space costs — nothing.
      // A leading blank still spends a line, so it is counted with the typed ones.
      var spent = lastMap.filter(function (ix) { return ix >= 0; }).length;
      var lead = typedCount() >= 2
        ? spent + ' of ' + maxLines + ' lines — a space doesn’t use one'
        : 'Up to ' + maxLines + ' lines — Return moves to the next line';
      els.caption.textContent = isPhone()
        ? lead + ' · ' + dims[0] + ' × ' + dims[1] + ' in, prints in red' + inkName
        : lead + ' · Shown at card proportions, a ' + dims[0] + ' × ' + dims[1] + ' inch ' + format + ' card, printed in red' + inkName;
    }

    // ── Pricing ─────────────────────────────────────────────────────────────
    function qty() { return Math.max(parseInt(els.qtyInput.value, 10) || minQty, 0); }

    // The header carries the ORDER TOTAL and nothing else (James, 2026-09-10:
    // "total only" — the per-card rate lives in the pricing modal, the count on
    // the stepper beside it). The review modal shows no money at all since the
    // same day: it is the proof step, and the price was already agreed to.
    var lastPricing = null;
    function renderPrice() {
      if (!doorCfg || !window.SaturnPricing) { els.headerPrice.textContent = ''; if (els.barMirror) els.barMirror.textContent = ''; return; }
      var p = window.SaturnPricing.computePricing(doorCfg, qty());
      lastPricing = p.valid ? p : null;
      if (!p.valid) { els.headerPrice.innerHTML = '<strong>' + p.validationMessage + '</strong>'; return; }
      var fmt = window.SaturnPricing.formatCents;
      els.headerPrice.innerHTML = '<strong>' + fmt(p.subtotalCents) + '</strong>';
      // Phone identity bar mirrors the count + total (the stepper is in the dock).
      if (els.barMirror) els.barMirror.innerHTML = qty() + ' cards · <strong>' + fmt(p.subtotalCents) + '</strong>';
      // Stepper note: the floor until the card carries text, then the per-card
      // rate (phone); desktop keeps the floor beside the total.
      if (els.qtyNote) {
        els.qtyNote.textContent = (hasTyped && isPhone()) ? fmt(p.unitEffectiveCents) + ' per card' : minQty + '-card min';
      }
    }

    // ── Pricing modal ("How pricing works") ─────────────────────────────────
    // The WHY of pricing as a live, actionable tier table: clicking a tier sets
    // the quantity, so the modal doubles as the upsell vehicle. Informational
    // only — it never gates any action. Since 2026-09-10 (ROADMAP #136) the fee
    // is folded into the per-card rate and NO fee or discount language renders
    // here: the rows are the price structure, never a markdown. A legacy config
    // that still carries setupFeeCents (a store not yet re-applied) keeps its
    // honest "includes the fee" clause so display == charge holds either way.
    function renderInfoModal() {
      if (!els.infoTiers || !doorCfg || !window.SaturnPricing) return;
      var fmt = window.SaturnPricing.formatCents;
      var sur = doorCfg.unitSurchargeCents || 0;
      var setup = doorCfg.setupFeeCents || 0;
      var tiers = doorCfg.tiers || [];
      var q = qty();
      if (els.infoSetup) {
        els.infoSetup.textContent = setup > 0
          ? 'Every order includes a one-time ' + fmt(setup) + ' hand-set fee. The per-card price steps down with quantity:'
          : 'Every card is hand-set in metal type and priced by the size of your order. The per-card rate steps down with quantity:';
      }
      els.infoTiers.innerHTML = tiers.map(function (t, i) {
        var next = tiers[i + 1];
        var label = next ? t.min + '–' + (next.min - 1) + ' cards' : t.min + '+ cards';
        var per = t.unitCents + sur;
        var current = q >= t.min && (!next || q < next.min);
        return '<button type="button" class="scz-tier' + (current ? ' scz-tier-current' : '')
          + '" data-tier-min="' + t.min + '"><span>' + label + '</span><span>' + fmt(per) + '/card</span></button>';
      }).join('');
      var p = window.SaturnPricing.computePricing(doorCfg, q);
      if (p.valid) {
        // Compose the nudge here rather than using p.nudge.message: the stock
        // message prints the raw tier unit, which on a legacy Door D config
        // excludes the per-card surcharge the rows above include — the same
        // "/card" figure must be the same number everywhere.
        var nudgeTxt = '';
        if (p.nudge && p.nudge.type === 'next_tier') {
          nudgeTxt = ' Add ' + p.nudge.cardsToAdd + ' more (' + (q + p.nudge.cardsToAdd)
            + ' total) to drop to ' + fmt(p.nudge.nextUnitCents + sur) + '/card'
            + (p.nudge.savesMoney ? ' — and pay less overall.' : '.');
        }
        els.infoTotal.innerHTML = 'Your order — <strong>' + q + ' cards · ' + fmt(p.subtotalCents) + '</strong>'
          + (setup > 0 ? ' (effective ' + fmt(p.unitEffectiveCents) + '/card with the hand-set fee).' : '.')
          + nudgeTxt;
      } else {
        els.infoTotal.textContent = '';
      }
      return p;
    }

    // "Interested — need to know more?" (James, 2026-09-10): the white-glove
    // on-ramp, a plain mailto in the left rail. Counted so the funnel can see
    // who reached for a person instead of the composer (build first, 2 keys).
    [].slice.call(document.querySelectorAll('.js-scz-email')).forEach(function (a) {
      a.addEventListener('click', function () {
        beacon.emit('email_clicked', Object.assign(buildMeta(root), { qty: qty() }), { now: true });
      });
    });

    // "Need design assistance?" (#223, 2026-10-05) — replaces the mailto above
    // while the set_for_you switch is open: the request sheet opens straight at
    // its words step with whatever the customer has composed carried over (and
    // the style, for the future auto-compose), no back arrow. Closing it returns
    // here with the draft untouched — the sheet never writes to the composer.
    if (els.assist) {
      els.assist.addEventListener('click', function () {
        var PS = window.SaturnPersonalizeSheet;
        var c = null;
        try { c = composition(); } catch (e) { c = null; }
        var message = c && c.lines ? c.lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() : '';
        beacon.emit('assist_click', Object.assign(buildMeta(root), { qty: qty(), typed: message ? 1 : 0 }), { now: true });
        if (!PS) return;
        var nameEl = root.querySelector('.scz-design-name');
        var thumbEl = root.querySelector('.scz-design-thumb');
        PS.openRequest({
          source: 'composer',
          handle: root.getAttribute('data-product-handle') || null,
          design: nameEl ? (nameEl.textContent || '').trim() : '',
          url: location.pathname,
          thumb: thumbEl ? { src: thumbEl.currentSrc || thumbEl.src } : null,
          prefill: { message: message, qty: qty() },
          compose: { face: (c && c.face) || '', size: (c && c.size) || '', placement: (c && c.placement) || '', qty: String(qty()) },
          opener: els.assist
        });
      });
    }

    if (els.infoModal) {
      // One trigger, one modal: the left rail's "How pricing works" link under
      // the WHAT prose (the header trigger was retired earlier).
      [].slice.call(document.querySelectorAll('.js-scz-how')).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var p = renderInfoModal();
          els.infoModal.classList.add('scz-open');
          // #124c (2026-09-10): the pricing-presentation trace. Which price
          // FRAMING a visitor met is the build stamp (first key, never dropped);
          // this row says they opened it and at what quantity/rate. Kept under
          // the 8-key meta cap: build + qty + unit + total = 4.
          beacon.emit('pricing_viewed', Object.assign(buildMeta(root), {
            qty: qty(),
            unit: p && p.valid ? p.unitEffectiveCents : 0,
            total: p && p.valid ? p.subtotalCents : 0
          }), { now: true });
        });
      });
      els.infoClose.addEventListener('click', function () { els.infoModal.classList.remove('scz-open'); });
      // The × is pure dismissal (2026-08-21). Same close path as the confirm —
      // a tier click has ALREADY written the quantity, so there is nothing to
      // roll back and the two differ in meaning, not in effect.
      var infoX = document.getElementById('scz-info-x');
      if (infoX) infoX.addEventListener('click', function () { els.infoModal.classList.remove('scz-open'); });
      els.infoModal.addEventListener('click', function (e) {
        if (e.target === els.infoModal) els.infoModal.classList.remove('scz-open');
      });
      els.infoTiers.addEventListener('click', function (e) {
        var btn = e.target.closest('button[data-tier-min]');
        if (!btn) return;
        els.qtyInput.value = Math.max(parseInt(btn.getAttribute('data-tier-min'), 10), minQty);
        syncAll(); // renderInfoModal re-runs inside syncAll while the modal is open
      });
    }

    function stepQty(delta) {
      els.qtyInput.value = Math.max(qty() + delta, minQty);
      syncAll();
    }
    els.qtyDec.addEventListener('click', function () { stepQty(-1); });
    els.qtyInc.addEventListener('click', function () { stepQty(1); });
    els.qtyInput.addEventListener('change', function () {
      if (qty() < minQty) els.qtyInput.value = minQty;
      syncAll();
    });

    // ── Form properties ─────────────────────────────────────────────────────
    function setProp(name, value) {
      var sel = 'input[name="properties[' + name + ']"]';
      var input = els.form.querySelector(sel);
      if (value === null) { if (input) input.remove(); return; }
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + name + ']';
        els.form.appendChild(input);
      }
      input.value = value;
    }

    function syncFormProps() {
      els.formQty.value = String(qty());
      if (lastValidity.ok) {
        setProp('_door', 'D');
        setProp('_lp_font', currentFace);
        setProp('_lp_size', currentSize);
        setProp('_lp_placement', currentPlacement);
        activeInputs().forEach(function (inp, i) { setProp('_lp_line_' + (i + 1), inp.value); });
        // #155: the space after each line — "g1,g2,g3", 0 none · 1 small · 2 large.
        // The proof payload is the render source; this keeps the cart proof and
        // edit-from-cart honest, and absent means none (every order before #155).
        var ag = appliedGaps();
        setProp('_lp_spaces', ag.some(function (g) { return g > 0; }) ? ag.join(',') : null);
        setProp('_lp_catalog', String(catalog.catalogVersion));
        scheduleProof();
      } else {
        ['_door', '_lp_font', '_lp_size', '_lp_placement', '_lp_spaces', '_lp_catalog', '_proof_ref', '_lp_reviewed']
          .forEach(function (n) { setProp(n, null); });
        for (var i = 1; i <= els.inputs.length; i++) setProp('_lp_line_' + i, null);
        proofSeq++;
      }
    }

    // ── Draft autosave (design-agnostic — powers the design-swap flow) ─────
    // The indicator is PERSISTENT (owner: autosave wasn't obvious): it shows
    // "Saving…" while typing and settles on "Saved automatically ✓".
    function setDraftState(text) {
      els.draftState.textContent = text;
      els.draftState.classList.toggle('scz-on', !!text);
    }

    function writeDraft() {
      if (restoring || editLineKey) return false;
      var lines = activeInputs().map(function (i) { return i.value; });
      // A draft exists only once a MESSAGE exists. With Garamond as the
      // standing default, face/size alone no longer signal intent — without
      // this guard every casual visit would mint a draft and a false
      // "personalization in progress" resume banner.
      if (!lines.some(function (l) { return l.trim(); })) return false;
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({
          face: currentFace, size: currentSize, placement: currentPlacement,
          lines: lines, gaps: appliedGaps(), styleTouched: styleTouched, qty: qty(), savedAt: new Date().toISOString(),
          // Last design worked on — lets the collection resume banner link
          // straight back to it. Draft stays design-agnostic (updates on swap).
          handle: root.getAttribute('data-product-handle') || null,
          title: (document.querySelector('.scz-title') || {}).textContent || null,
        }));
        return true;
      } catch (e) { return false; }
    }

    function saveDraft() {
      // In edit-from-cart mode the CART LINE is the persistence — writing a
      // parallel draft would stomp the "Editing your cart item" label and
      // leave a shadow copy behind for the resume banner.
      if (restoring || editLineKey) return;
      var hasContent = activeInputs().some(function (i) { return i.value.trim(); });
      if (hasContent) setDraftState('Saving…');
      clearTimeout(draftTimer);
      draftTimer = setTimeout(function () {
        if (writeDraft()) setDraftState('Saved automatically ✓');
      }, 500);
    }

    // Save & exit: flush the pending debounce synchronously, then follow the
    // link (draft restores on any Personalize page).
    //
    // In edit-from-cart mode there is no draft — the cart line is the
    // persistence — so "save" writes the edited composition back onto the
    // line in place via /cart/change.js, which replaces a line's properties
    // WHOLESALE (never merges). The payload is therefore harvested complete
    // from the same hidden form inputs the review-modal confirm submits, so
    // the two landing paths cannot drift; _lp_reviewed stays stamped because
    // the cart's double-check flow is the review on this path (owner,
    // 2026-09-02). An invalid composition never overwrites the line — the
    // exit is blocked with the same status panel the Add-to-cart gate uses —
    // and a failed update keeps the page here too: navigating anyway would
    // discard the edit silently, which is the exact bug this path fixes.
    var exitBtn = document.getElementById('scz-exit');
    var exitSaving = false;
    if (exitBtn) {
      exitBtn.addEventListener('click', function (e) {
        if (!editLineKey) {
          clearTimeout(draftTimer);
          writeDraft();
          clearEditCtx(); // leaving deliberately ends any edit session
          return; // the anchor navigates on its own
        }
        e.preventDefault();
        if (exitSaving) return;
        validateAll();
        if (!lastValidity.ok) {
          if (lastValidity.reason !== 'inventory') {
            firstIssueText = 'Write at least one line of your message — your cart keeps the original until you save.';
          }
          updateMeter();
          focusLine(lastValidity.reason === 'inventory' ? firstIssueLine : 0);
          return;
        }
        syncFormProps(); // props must reflect THIS composition before harvest
        var props = { _lp_reviewed: 'yes' };
        var propInputs = els.form.querySelectorAll('input[name^="properties["]');
        for (var i = 0; i < propInputs.length; i++) {
          var m = propInputs[i].name.match(/^properties\[(.+)\]$/);
          if (m) props[m[1]] = propInputs[i].value;
        }
        exitSaving = true;
        setDraftState('Saving…');
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editLineKey, quantity: qty(), properties: props }),
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart change ' + r.status);
            beacon.emit('cart_added', { qty: qty(), edit: true, via: 'save_exit' }, { now: true });
            beacon.flush(); // navigation follows immediately — get the batch out
            clearEditCtx(); // the edit landed; the session ends here
            location.href = exitBtn.href;
          })
          .catch(function () {
            exitSaving = false;
            setDraftState('Couldn’t save — try again');
          });
      });
    }

    // Apply a saved composition {face,size,placement,lines,qty} — shared by
    // draft restore and edit-from-cart. Gracefully drops anything the current
    // catalog no longer offers (retired face, size under the floor).
    function applyComposition(d) {
      restoring = true;
      if (d.placement && catalog.messagePlacement.options[d.placement]) {
        applyPlacementUI(d.placement);
      }
      if (d.face && catalog.faces[d.face] && catalog.faces[d.face].webFont && !catalog.faces[d.face].menuExcluded
        && menuSizes(d.face).length > 0) {
        setFace(d.face);
        var sizeOk = d.size && menuSizes(d.face).indexOf(d.size) !== -1;
        if (sizeOk) {
          setSize(d.size, false);
          (d.lines || []).slice(0, maxLines).forEach(function (l, i) {
            els.inputs[i].value = typeof l === 'string' ? l : '';
          });
          // #155: the saved spaces, then the conversion — a draft from before
          // line spaces carries its space as an empty middle line.
          gaps = readGaps(d.gaps);
          restoreConverted = normalizeRows();
        }
      }
      // A returning composer has met the page: the dock goes straight to Review.
      styleTouched = true;
      if (d.qty && d.qty >= minQty) els.qtyInput.value = d.qty;
      restoring = false;
      validateAll();
      syncAll();
    }
    var restoreConverted = 0;

    // The card arrives compose-ready — click and type, zero picks required
    // (owner 2026-07-18). Default = the FIRST face in the menu at its smallest
    // size (owner 2026-09-12: Bodoni Bold Italic 18pt, because it is what the
    // list shows first — the default should match what the eye lands on).
    // Runs AFTER the restore paths so a saved draft's or cart line's face wins.
    function ensureDefaultFace() {
      if (currentFace || !menuFaces.length) return;
      var def = menuFaces[0];
      setFace(def);
      var sizes = menuSizes(def);
      if (sizes.length) setSize(sizes[0], false);
      validateAll();
      syncAll();
    }

    function restoreDraft() {
      var raw = null;
      try { raw = localStorage.getItem(DRAFT_KEY); } catch (e) { return; }
      if (!raw) return;
      var d;
      try { d = JSON.parse(raw); } catch (e) { return; }
      applyComposition(d);
      showResumeBand(d.savedAt);
      beacon.emit('draft_restored', null, { once: true });
    }

    // Return visit (#138): one line under the bar says the message came back,
    // with the date it was saved and a way to start over. Replaces the
    // transient "Draft restored ✓" state text.
    function showResumeBand(savedAt) {
      if (!els.resumeBand) return;
      var when = '';
      try {
        if (savedAt) {
          var dt = new Date(savedAt);
          if (!isNaN(dt.getTime())) when = ' on ' + dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        }
      } catch (e) { /* no date, no harm */ }
      els.resumeText.textContent = (isPhone()
        ? 'We restored the message you saved' + when + '.'
        : 'We restored the message you saved' + when + ' — pick up where you left off, or start over.')
        + (restoreConverted ? ' The blank line in it is now a space, and your ' + (['first', 'second', 'third', 'fourth', 'fifth', 'sixth'][maxLines - 1] || 'last') + ' line is free.' : '');
      els.resumeBand.hidden = false;
    }
    function hideResumeBand() { if (els.resumeBand) els.resumeBand.hidden = true; }
    if (els.resumeReset) els.resumeReset.addEventListener('click', function () {
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* fine */ }
      activeInputs().forEach(function (i) { i.value = ''; });
      gaps = noGaps();
      hideResumeBand();
      setDraftState('');
      validateAll();
      syncAll();
      focusLine(0);
    });

    // Edit-from-cart (owner fast-follow 2026-07-17): ?edit-line=<key> prefills
    // FROM THE CART LINE (the line is the truth being edited, not the draft);
    // the confirm handler then removes that line before adding the new one so
    // the cart never holds both versions.
    //
    // The edit context also RELAYS across a design swap (James bug 2026-07-24):
    // "Choose a different design" routes through the collection, and product
    // links there can't carry ?edit-line — without a relay the new design's
    // page loaded editless, so confirm ADDED a line instead of replacing, and
    // the cart grew a duplicate.
    //
    // The relay is ONE HOP (owner bug 2026-09-04). It used to be written on
    // every edit-mode load and cleared only on confirm, Save & exit, a missing
    // line or a 30-minute timeout — so any OTHER route to another design inside
    // that window (header nav, back button, cart page, collection grid) reopened
    // edit mode for the OLD line on the NEW product: "Editing your cart item"
    // over a fresh design, and confirm would have removed the original line and
    // added a different product. Now it is written only when the swap link is
    // clicked in edit mode, spent by the next composer load however that load
    // resolves, and the landing page pins the key into its own URL
    // (?edit-line=) so a reload keeps the session with no relay at all.
    // sessionStorage keeps it per-tab; the TTL is a backstop for a hop that
    // never lands.
    var EDIT_CTX_KEY = 'sp-edit-line-ctx-v1';
    var EDIT_CTX_TTL_MS = 30 * 60 * 1000;
    var editLineKey = null;

    // Read AND clear — a relay is spent the moment any composer page reads it.
    function takeEditCtx() {
      try {
        var raw = sessionStorage.getItem(EDIT_CTX_KEY);
        if (!raw) return null;
        clearEditCtx();
        var ctx = JSON.parse(raw);
        if (!ctx.key || !ctx.at || Date.now() - ctx.at > EDIT_CTX_TTL_MS) return null;
        return ctx.key;
      } catch (e) { return null; }
    }
    function writeEditCtx(key) {
      try { sessionStorage.setItem(EDIT_CTX_KEY, JSON.stringify({ key: key, at: Date.now() })); } catch (e) { /* fine */ }
    }
    function clearEditCtx() {
      try { sessionStorage.removeItem(EDIT_CTX_KEY); } catch (e) { /* fine */ }
    }

    // "Choose a different design" is the ONE path that carries an edit session
    // across the collection page. Written at click time, so nothing else that
    // leaves this page can hand the session to an unrelated design.
    var swapLink = root.querySelector('.scz-swap');
    if (swapLink) {
      swapLink.addEventListener('click', function () {
        if (editLineKey) writeEditCtx(editLineKey);
      });
    }

    function restoreFromCartLine(key, viaRelay) {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var item = (cart.items || []).find(function (i) { return i.key === key; });
          if (!item) { clearEditCtx(); restoreDraft(); ensureDefaultFace(); return; }
          var p = item.properties || {};
          applyComposition({
            face: p._lp_font, size: p._lp_size, placement: p._lp_placement,
            lines: [1, 2, 3, 4, 5, 6].map(function (n) { return p['_lp_line_' + n] || ''; }),
            gaps: p._lp_spaces || null,
            qty: item.quantity,
          });
          editLineKey = key;
          // A relayed hop lands without ?edit-line in the URL. Pin it, so a
          // reload on this design keeps editing THIS line (the relay is spent)
          // and the address says what the page is doing.
          if (viaRelay) {
            try {
              var u = new URL(location.href); u.searchParams.set('edit-line', key);
              history.replaceState(null, '', u.pathname + u.search + u.hash);
            } catch (e) { /* fine */ }
          }
          // An edit session exits back to the CART it came from (owner,
          // 2026-09-02), not the collection. Retargeting the href keeps the
          // anchor honest (status bar, modifier clicks) and gives the save
          // handler one place to read the destination from.
          if (exitBtn) exitBtn.setAttribute('href', '/cart');
          setDraftState('Editing your cart item');
          ensureDefaultFace(); // if the line's face was retired, fall back to the default
        })
        .catch(function () { restoreDraft(); ensureDefaultFace(); });
    }

    // ── One sync point: validate-dependent renders + props + draft ─────────
    // Save & exit reads idle until the card carries text (owner 2026-09-10):
    // on a blank card there is nothing to save. Edit-from-cart is exempt — the
    // line already exists and "save" writes it back, blank or not.
    function updateExitState() {
      var exit = document.getElementById('scz-exit');
      if (!exit) return;
      var blank = !activeInputs().some(function (i) { return i.value.trim(); });
      exit.classList.toggle('scz-exit-idle', blank && !editLineKey);
    }

    function syncAll() {
      syncFormProps();
      renderEmulator();
      renderPrice();
      updateExitState();
      if (els.infoModal && els.infoModal.classList.contains('scz-open')) renderInfoModal();
      saveDraft();
      updateProofLink();
    }

    // ── Add to cart: review modal gate (no-proof policy) ───────────────────
    // Focus a line and bring the card into view (the dock covers the bottom
    // of a phone viewport; the card sits above it).
    function focusLine(idx) {
      var inp = activeInputs()[Math.max(0, idx)];
      if (!inp) return;
      try { els.card.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* fine */ }
      try { inp.focus({ preventScroll: true }); } catch (e) { inp.focus(); }
    }
    els.atc.addEventListener('click', function () {
      if (normalizeRows()) syncAll();
      validateAll();
      // #155 soft sequence: "Style it" opens the style sheet. Never a gate —
      // the step words above the button reach Review directly.
      if (dockStep() === 2) {
        markInteraction();
        beacon.emit('style_step', null, { once: true });
        openStyleSheet();
        return;
      }
      if (!lastValidity.ok) {
        // Phone, blank card: the button SAYS "Start typing", so the tap is the
        // intended first move, not a blocked review — it counts as interaction
        // and lands the caret on line 1. Every other gated press keeps its
        // `atc_blocked` trace (the only positive signal a never-composed
        // visitor leaves) and points at what to fix.
        if (lastValidity.reason === 'incomplete' && !!currentCfg) {
          markInteraction();
          focusLine(0);
          return;
        }
        atcClicked = true;
        beacon.emit('atc_blocked', { reason: lastValidity.reason }, { now: true });
        if (lastValidity.reason === 'incomplete') {
          firstIssueText = !currentCfg ? 'Choose a typeface and size, then write your message.' : 'Write at least one line of your message.';
          updateMeter();
          focusLine(0);
        } else {
          updateMeter();
          focusLine(firstIssueLine);
        }
        return;
      }
      atcClicked = true; // set on ANY press, valid or blocked — the abandon
      // signal is about never reaching for the button, which is a different
      // failure from pressing it and being turned away (that is `atc_blocked`).
      beacon.emit('review_opened', { qty: qty() }, { now: true });
      // Populate the review echo in the true face + placement metadata
      var css = fontCss(currentFace);
      // #155: one element per composed entry — a space is a gap of its own
      // height (the core's spaceEm), never a row of type.
      var echo = compose();
      els.modalEcho.textContent = '';
      // The block sits inside the card box exactly as the emulator seats it
      // (renderEmulator): top at the placement line, the band's width, the
      // core's anchor transform. Sized once the modal is open (below) \u2014 the
      // transform needs the rendered width.
      var block = document.createElement('div');
      block.className = 'scz-modal-echo-block';
      var textRows = [], spaceRows = [];
      echo.lines.forEach(function (l, i) {
        var row = document.createElement('div');
        if (l.trim() || echo.map[i] >= 0) { row.textContent = l || '\u00a0'; textRows.push(row); }
        else { spaceRows.push({ el: row, half: echo.halfSpaces.indexOf(i) !== -1 }); row.setAttribute('aria-hidden', 'true'); }
        block.appendChild(row);
      });
      els.modalEcho.appendChild(block);
      block.style.fontFamily = css.family;
      block.style.fontWeight = css.weight;
      block.style.fontStyle = css.style;
      block.style.color = inkHex;
      var fmtTotal = (lastPricing && window.SaturnPricing) ? ' · ' + window.SaturnPricing.formatCents(lastPricing.subtotalCents) : '';
      els.modalMeta.textContent = currentFace + ' · ' + currentSize + ' · '
        + (currentPlacement === 'center' ? 'centered' : 'classic placement') + ' · ' + qty() + ' cards' + fmtTotal;
      els.modal.classList.add('scz-open');
      // The echo is the card at proportion: scale the type to the echo's
      // rendered width exactly as the emulator scales it to the card's.
      var dimsE = Core.cardDims(catalog, format);
      var scaleE = Core.emulatorScale(els.modalEcho.clientWidth, dimsE);
      var ptE = parseInt(currentSize, 10);
      var pxE = ptE * PX_PER_PT * scaleE;
      var rowHE = Math.ceil(pxE * Core.lineHeight(ptE));
      var optE = Core.placementOption(catalog, currentPlacement);
      var bandPicaE = limits ? limits.limitPica : (catalog.maxLineMeasurePica || 22.5);
      block.style.fontSize = pxE + 'px';
      block.style.lineHeight = rowHE + 'px';
      block.style.top = (optE.verticalCenterRatio * 100) + '%';
      block.setAttribute('data-placement-ratio', String(optE.verticalCenterRatio)); // the DOM smoke's reference
      block.style.width = (Core.bandGeometry(dimsE, bandPicaE, optE).widthRatio * 100) + '%';
      var blockH = rowHE * textRows.length;
      spaceRows.forEach(function (s) { var em = Core.spaceEm(ptE, s.half); s.el.style.height = em + 'em'; blockH += pxE * em; });
      block.style.transform = Core.emulatorTransform(optE.anchor, rowHE, PX_PER_PT * scaleE, blockH, Core.CENTER_BOX_ROWS);
    });

    els.modalBack.addEventListener('click', function () { els.modal.classList.remove('scz-open'); });
    els.modal.addEventListener('click', function (e) { if (e.target === els.modal) els.modal.classList.remove('scz-open'); });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (els.styleSheet && els.styleSheet.classList.contains('scz-open')) {
        closeStyleSheet();
      } else if (els.infoModal && els.infoModal.classList.contains('scz-open')) {
        els.infoModal.classList.remove('scz-open');
      } else if (els.modal.classList.contains('scz-open')) {
        els.modal.classList.remove('scz-open');
      }
    });

    els.modalConfirm.addEventListener('click', function () {
      if (!lastValidity.ok) { els.modal.classList.remove('scz-open'); return; }
      beacon.emit('cart_added', { qty: qty(), edit: !!editLineKey }, { now: true });
      beacon.flush(); // the form submit navigates away — get the batch out now
      setProp('_lp_reviewed', 'yes');
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* fine */ }
      clearEditCtx(); // the edit session ends here whichever path submits
      if (!editLineKey) {
        // Composer-side cart event (see klaviyoTrack). An edit is not a new
        // add, so only the fresh path reports. The add never waits LONG on
        // analytics: the submit runs when the track settles, on its failure,
        // or on a hard timer — whichever comes first, exactly once.
        // ⚠ The submit is a NAVIGATION, which aborts any request still in
        // flight — the owner's first live add (2026-09-04 02:1x EDT) produced
        // no event under a 900 ms cap. The cap is now 3 s: typical delivery
        // is well under a second, and the customer sees at most a short
        // disabled button on a failing network.
        els.modalConfirm.disabled = true;
        var submitted = false;
        var go = function () { if (submitted) return; submitted = true; els.form.submit(); };
        try { klaviyoTrack('Personalized Card Added to Cart', cartEventProps()).then(go, go); } catch (e) { go(); }
        setTimeout(go, 3000);
        return;
      }
      // Editing: remove the original line first so the cart never holds both
      // versions. If the removal fails (line already gone, network), the add
      // still proceeds — a duplicate line is recoverable, a lost edit is not.
      els.modalConfirm.disabled = true;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editLineKey, quantity: 0 }),
      })
        .catch(function () { /* proceed regardless */ })
        .then(function () { els.form.submit(); });
    });

    // ── #81 abandon signal ────────────────────────────────────────────────
    // The Aug-9 funnel read found 6 of 7 non-owner composers never pressed Add
    // to cart at all — `atc_blocked` was 0, so they were not turned away, they
    // never reached for it. That drop was only ever visible as an ABSENCE,
    // inferred by subtracting events that never arrived. This records it
    // positively: the session reached a valid, printable composition and then
    // the page went away with the button untouched.
    //
    // At most once per session. `pagehide` is the true terminal signal but is
    // unreliable on mobile — exactly the population in question — so a hidden
    // `visibilitychange` arms it too. That means a tab switch can fire it
    // before the visitor comes back and converts, so `compose_abandoned` and
    // `cart_added` in the same session is "left and returned", not a
    // contradiction. Count sessions carrying abandon-without-cart, not raw
    // abandons.
    function markAbandon() {
      if (atcClicked || !lastValidity.ok) return;
      beacon.emit('compose_abandoned', Object.assign(deviceMeta(), {
        proofs: proofsMinted, qty: qty(), face: currentFace, size: currentSize,
      }), { once: true });
      beacon.flush(); // the page is going away — emit()'s 4s timer will not run
    }
    window.addEventListener('pagehide', markAbandon);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') markAbandon();
    });

    // ── #81 fold signals ──────────────────────────────────────────────────
    // The Aug-10 fold read (mobile, 375×812) found the sticky buy bar — title,
    // $287.50, the 50-card minimum, Add to cart — pinned above the fold from
    // load, while `.scz-rail`, which carries the card's actual artwork,
    // "Choose a different design" and the HOW PERSONALIZATION WORKS copy,
    // starts at y≈1084. Everything that justifies the price sits below
    // everything that asks for it.
    //
    // That was one viewport at one width, measured by hand. These turn it into
    // data at the widths visitors actually use:
    //   `atc_seen`    — expected near 100%. It retires "they never scrolled to
    //                   the button" as an explanation instead of assuming it
    //                   away, which is the whole reason the Aug-10 session did
    //                   NOT ship the originally-planned beacon by that name
    //                   alone: on this layout it would have been ~100% and
    //                   diagnosed nothing.
    //   `design_seen` — did the session ever reach the artwork and the craft
    //                   copy? For the 32-of-40 visitors who left without typing
    //                   a character, this separates "saw what it was and passed"
    //                   from "never found out what they were buying".
    //
    // `proofs` orders the pair against composition: `design_seen` at proofs 0 is
    // someone who scrolled before writing, which reads very differently from
    // someone who composed first and only then went looking for the artwork.
    (function observeFold() {
      if (!window.IntersectionObserver) return; // telemetry never breaks composing
      var targets = [
        { el: els.atc, event: 'atc_seen', ratio: 0 },
        // Prefer the artwork thumb; fall back to the rail that contains it, so
        // a design without a mock image still reports the copy being reached.
        { el: root.querySelector('.scz-design-thumb') || document.getElementById('scz-info-block'), event: 'design_seen', ratio: 0.25 },
      ].filter(function (t) { return t.el; });
      if (!targets.length) return;
      var pending = targets.length;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var t = targets.filter(function (x) { return x.el === entry.target; })[0];
          if (!t || t.fired || !entry.isIntersecting) return;
          if (entry.intersectionRatio < t.ratio) return; // a 1px sliver is not "seen"
          t.fired = true;
          pending--;
          beacon.emit(t.event, Object.assign(deviceMeta(), { proofs: proofsMinted }), { once: true });
          io.unobserve(entry.target);
        });
        if (pending <= 0) io.disconnect();
      }, { threshold: [0, 0.25] });
      targets.forEach(function (t) { io.observe(t.el); });
    })();

    // ── Story modal open (2026-08-24 handoff, option (a): PDP host only) ────
    // Second listener on the same .js-scz-story button the story snippet's own
    // IIFE binds — the snippet stays the thing that OPENS the modal, so it
    // remains portable to hosts with no beacon; this one only observes.
    // NOT routed through markInteraction(): opening the story is not
    // composing, and folding it into first_interaction would corrupt the fold
    // read's never-typed denominator. Single event, no story_closed/dwell —
    // it answers "did this session open the story", nothing more.
    (function observeStory() {
      [].slice.call(document.querySelectorAll('.js-scz-story')).forEach(function (btn) {
        btn.addEventListener('click', function () {
          beacon.emit('story_opened', null, { once: true });
        });
      });
    })();

    // ── Proof draft (2026-09-03): a nudge in a proof's clothing ────────────
    // Requirements doc, Stream C. The customer who is not ready gets the card
    // as it will print, emailed, with a link that brings the draft back on any
    // device. Three triggers (standing header link · desktop exit intent ·
    // touch-device idle), one modal, once per session, only with a valid
    // composition, never in edit-from-cart, never while another modal is up,
    // never for a proof she already has. The PNG is drawn HERE from the same
    // state the preview renders — face, size, placement, ink, card
    // proportions — so what she receives is what she saw. Nothing on this
    // path can block Add to cart: every failure leaves the local draft where
    // it was and says so.
    // Kill switch: shop metafield custom.proof_drafts, read by the section into
    // data-proof-drafts. Anything but "open" hides the link and silences the
    // offer; the ?draft= resume path stays live regardless.
    var proofDraftsOpen = root.getAttribute('data-proof-drafts') === 'open';
    var proofOffered = false, proofSaving = false, proofSavedHash = null;
    try { proofOffered = !!sessionStorage.getItem(PROOF_OFFERED_KEY); } catch (e) {}
    try { proofSavedHash = localStorage.getItem(PROOF_SAVED_KEY); } catch (e) {}

    function compositionHash() { return Core.compositionKey(composition()); }
    function proofSavedForCurrent() { return !!proofSavedHash && proofSavedHash === compositionHash(); }
    function knownEmail() {
      var e = root.getAttribute('data-customer-email') || '';
      if (!e) { try { e = localStorage.getItem(PROOF_EMAIL_KEY) || ''; } catch (err) { /* fine */ } }
      return e;
    }
    function updateProofLink() {
      if (!els.proofLink) return;
      var eligible = proofDraftsOpen && lastValidity.ok && !editLineKey;
      els.proofLink.hidden = !eligible;
      if (!eligible) return;
      if (proofSavedForCurrent()) {
        els.proofLink.textContent = 'Proof sent ✓';
        els.proofLink.classList.add('scz-proof-sent');
      } else {
        els.proofLink.textContent = 'Email me a proof';
        els.proofLink.classList.remove('scz-proof-sent');
      }
    }
    function proofModalOpen() { return !!(els.proofModal && els.proofModal.classList.contains('scz-open')); }
    function otherModalOpen() {
      return !!((els.modal && els.modal.classList.contains('scz-open'))
        || (els.infoModal && els.infoModal.classList.contains('scz-open')));
    }
    function markOffered() {
      proofOffered = true;
      try { sessionStorage.setItem(PROOF_OFFERED_KEY, '1'); } catch (e) { /* fine */ }
    }

    function openProofOffer(trigger) {
      if (!els.proofModal || proofModalOpen()) return;
      var known = knownEmail();
      els.proofAsk.hidden = false; els.proofDone.hidden = true;
      els.proofStatus.textContent = ''; els.proofStatus.classList.remove('scz-proof-status--ok');
      els.proofEmail.value = known;
      els.proofEmail.readOnly = !!known;
      els.proofChange.hidden = !known;
      els.proofCopy.textContent = known
        ? 'Need some time to think it over? Don’t worry, we’ve got you. We’ll send a link to your draft with a full-resolution proof of your greeting to the address below.'
        : 'Need some time to think it over? Don’t worry, we’ve got you. Enter your email and we’ll send you a link to your draft with a full-resolution proof of your greeting.';
      els.proofSend.disabled = false;
      els.proofModal.classList.add('scz-open');
      if (!known) { try { els.proofEmail.focus(); } catch (e) { /* fine */ } }
      // `proofs` = composition depth at the moment of the offer (#170). Without
      // it the depth of an offer has to be reconstructed by joining this row to
      // the proof_minted rows of the same session, which is a census, not a
      // read. Six keys total — under the endpoint's 8-key cap.
      beacon.emit('proof_offer_shown', Object.assign(deviceMeta(), { trigger: trigger, known: !!known, qty: qty(), proofs: proofsMinted }), { now: true });
    }
    function closeProofOffer(kind) {
      if (!els.proofModal) return;
      els.proofModal.classList.remove('scz-open');
      if (kind) beacon.emit('proof_offer_dismissed', { kind: kind }, { now: true });
    }
    // The automatic triggers: once per session, and only when an offer would
    // be honest — a valid composition, no cart line being edited, no other
    // modal up, and not for a proof she already has.
    function maybeOffer(trigger) {
      if (!proofDraftsOpen) return;
      if (proofOffered || proofSaving || proofModalOpen() || otherModalOpen()) return;
      if (!lastValidity.ok || editLineKey || proofSavedForCurrent()) return;
      // Depth gate, BEFORE markOffered on purpose: a blocked early trigger must
      // not burn the one offer this session gets. Exit intent at ten seconds
      // simply does not fire, and the same visitor's next exit intent — once
      // she has actually composed — still can.
      if (proofsMinted < PROOF_MIN_EDITS) return;
      markOffered();
      openProofOffer(trigger);
    }
    // Desktop exit intent: the cursor leaves through the TOP of the viewport
    // (toward tabs, the address bar, the back button). Sides and bottom are
    // not intent — a second monitor, a dock. "Choose a different design" and
    // Save & exit are clicks inside the page and never trip this.
    document.addEventListener('mouseout', function (e) {
      if (e.relatedTarget) return;
      if (typeof e.clientY !== 'number' || e.clientY > 0) return;
      maybeOffer('exit');
    });
    // Touch devices have no cursor to read; idle with a valid composition
    // stands in for it. Any interaction resets the clock.
    (function idleTrigger() {
      if (!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches)) return;
      var timer = null;
      function arm() {
        clearTimeout(timer);
        timer = setTimeout(function () { maybeOffer('idle'); }, PROOF_IDLE_MS);
      }
      ['touchstart', 'scroll', 'input', 'keydown', 'click'].forEach(function (ev) {
        document.addEventListener(ev, arm, { passive: true });
      });
      arm();
    })();
    if (els.proofLink) els.proofLink.addEventListener('click', function () {
      if (!proofDraftsOpen || proofSavedForCurrent()) return;
      markOffered();
      openProofOffer('link');
    });
    if (els.proofSkip) els.proofSkip.addEventListener('click', function () { closeProofOffer('skip'); });
    if (els.proofX) els.proofX.addEventListener('click', function () {
      closeProofOffer(els.proofDone && els.proofDone.hidden ? 'close' : null);
    });
    if (els.proofDoneClose) els.proofDoneClose.addEventListener('click', function () { closeProofOffer(null); });
    if (els.proofModal) els.proofModal.addEventListener('click', function (e) {
      if (e.target === els.proofModal) closeProofOffer(els.proofDone && els.proofDone.hidden ? 'backdrop' : null);
    });
    if (els.proofChange) els.proofChange.addEventListener('click', function () {
      els.proofEmail.readOnly = false; els.proofChange.hidden = true;
      try { els.proofEmail.focus(); els.proofEmail.select(); } catch (e) { /* fine */ }
    });

    // The proof itself: drawn from state, not the DOM, at PROOF_DPI. Same
    // geometry as renderEmulator(): the placement line at verticalCenterRatio,
    // a 'baseline' anchor seating line 1's row on it, 'center' the capped
    // four-row box (geometry.ts); the core's whole-lead row (lineHeight); the catalog's ink. Flat paper rather than the
    // felt texture — a texture is noise to a PNG encoder and quadruples the
    // upload for no information the customer needs.
    function renderProofPng(dpi) {
      try {
        var dims = (catalog.cardDimensionsIn && catalog.cardDimensionsIn[format]) || (format === 'landscape' ? [7, 5] : [5, 7]);
        var W = Math.round(dims[0] * dpi), H = Math.round(dims[1] * dpi);
        var c = document.createElement('canvas'); c.width = W; c.height = H;
        var ctx = c.getContext('2d'); if (!ctx) return null;
        ctx.fillStyle = '#fffdf8'; ctx.fillRect(0, 0, W, H);
        var lw = Math.max(1, Math.round(dpi / 120));
        ctx.strokeStyle = 'rgba(26,20,16,0.18)'; ctx.lineWidth = lw; ctx.strokeRect(lw / 2, lw / 2, W - lw, H - lw);
        var css = fontCss(currentFace);
        var pt = parseInt(currentSize, 10);
        var px = pt * (dpi / 72);
        var rowH = px * Core.lineHeight(pt);
        ctx.font = css.style + ' ' + css.weight + ' ' + px + 'px ' + css.family;
        ctx.fillStyle = inkHex; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        // #155: the composed entries (spaces included) at the core's row
        // heights — the same arithmetic as the server's pngLayout.
        var comp = compose();
        var lines = comp.lines;
        var heights = Core.rowHeights(lines, comp.halfSpaces, px, pt);
        var blockH = heights.reduce(function (a, b) { return a + b; }, 0);
        var opt = ((catalog.messagePlacement && catalog.messagePlacement.options
          && catalog.messagePlacement.options[currentPlacement]) || { verticalCenterRatio: 0.3333, anchor: 'baseline' });
        // 'center': the same four-row box the emulator shows, capped at 18pt
        // (#153) and grown to fit a taller block — this used to true-centre EVERY block, so a short Centered
        // proof image sat lower than the emulator the customer approved by eye.
        var y0 = opt.anchor === 'baseline'
          ? opt.verticalCenterRatio * H - (heights[0] || rowH)
          : opt.verticalCenterRatio * H - Core.centerOffset(Core.centerBoxOffsetPt(parseInt(currentSize, 10), Core.CENTER_BOX_ROWS) * (dpi / 72), blockH);
        var yy = y0;
        lines.forEach(function (l, i) { if (l) ctx.fillText(l, W / 2, yy + heights[i] / 2); yy += heights[i]; });
        return c.toDataURL('image/png');
      } catch (e) { return null; }
    }
    function fontReady() {
      try {
        if (!document.fonts || !document.fonts.load) return Promise.resolve();
        var css = fontCss(currentFace);
        return Promise.race([
          document.fonts.load(css.style + ' ' + css.weight + ' 40px ' + css.family),
          new Promise(function (res) { setTimeout(res, 1500); }),
        ]);
      } catch (e) { return Promise.resolve(); }
    }
    // Klaviyo hand-off. Retail only — the object is absent on the test stores,
    // and that is fine: the draft is saved either way, only the email leg is
    // missing. ORDER MATTERS: identify must complete before track, or the
    // event attaches to whatever profile the browser cookie held before
    // (observed live 2026-09-03: the events call left 1.3 s BEFORE the
    // profiles call when both were queued with push()). The modern object
    // returns promises; the legacy queue does not, so it gets a delay.
    // ── Composer-side cart event (2026-09-04, ROADMAP #114 fallback) ────────
    // The composer adds via a native form post. Neither Klaviyo's onsite JS
    // nor Shopify's web pixel — the source of this store's "Added to Cart"
    // metric (proved from a live event: extra.standard.event_type =
    // product_added_to_cart) — observes that post, and even if one did, the
    // pixel's CartLine carries no line properties, so the composition could
    // never reach the cart email. So the composer reports the add itself as
    // an API metric of its own: Klaviyo keys metrics by name + integration,
    // so this can never double-enter the standard cart flow (which now
    // filters personalized products out anyway — #120). The composed-cart
    // flow triggers on this event and renders the message from it.
    // Anonymous browsers drop the event exactly as Klaviyo's own tracking
    // would; a proof-draft saver is already identified (klaviyoSend). No
    // identify here — the composer never knows the address on this path.
    var lastProofResumeUrl = null, lastProofImageUrl = null;

    function cartEventProps() {
      var g = function (a) { return root.getAttribute(a) || ''; };
      var abs = function (u) { return u.indexOf('//') === 0 ? 'https:' + u : u; };
      var lines = activeInputs().map(function (i) { return i.value; });
      var lastFilled = -1;
      lines.forEach(function (l, i) { if (l.trim()) lastFilled = i; });
      var p = (doorCfg && window.SaturnPricing) ? window.SaturnPricing.computePricing(doorCfg, qty()) : null;
      // No `$currency`: Klaviyo's client API reserves `$`-prefixed keys
      // (`$value` is the sanctioned one) and the pixel's copy is server-side.
      var props = {
        'Product Name': g('data-product-title'), ProductID: g('data-product-id'), VariantID: g('data-variant-id'),
        'Variant SKU': g('data-variant-sku'), Brand: g('data-product-vendor'), 'Product Type': g('data-product-type'),
        Quantity: qty(), ImageURL: abs(g('data-product-image')), URL: g('data-product-url'), CartURL: g('data-cart-url'),
        design_title: ((document.querySelector('.scz-title') || {}).textContent || g('data-product-title')).trim(),
        design_handle: g('data-product-handle'),
        face: currentFace, size: currentSize, placement: currentPlacement,
        line_1: lines[0] || '', line_2: lines[1] || '', line_3: lines[2] || '', line_4: lines[3] || '', line_5: lines[4] || '', line_6: lines[5] || '',
        message: lines.slice(0, lastFilled + 1).join('\n'),
        ink: inkHex,
        catalog_version: catalog ? String(catalog.catalogVersion) : null,
      };
      if (p && p.valid) { props.$value = Math.round(p.subtotalCents) / 100; props.Price = Math.round(p.unitEffectiveCents) / 100; }
      if (lastProofResumeUrl) { props.resume_url = lastProofResumeUrl; props.proof_image_url = lastProofImageUrl; }
      return props;
    }

    // Event only, bounded: resolves when Klaviyo's call settles or after
    // 2.8 s, whichever is first, so a caller can chain a navigation on it.
    // (Klaviyo's track() resolves only once its request has COMPLETED, so
    // waiting for it is what keeps the following navigation from aborting
    // the request.)
    function klaviyoTrack(eventName, props) {
      var settled = Promise.resolve();
      try {
        var k = window.klaviyo;
        if (k && typeof k.track === 'function') {
          settled = Promise.resolve(k.track(eventName, props)).catch(function () { /* never breaks the add */ });
        } else {
          var q = window._learnq || (k && typeof k.push === 'function' ? k : null);
          if (q) q.push(['track', eventName, props]);
        }
      } catch (e) { /* never breaks the add */ }
      var wait = new Promise(function (resolve) { setTimeout(resolve, 2800); });
      return Promise.race([settled, wait]);
    }

    function klaviyoSend(email, profileProps, eventName, eventProps) {
      try {
        var k = window.klaviyo;
        if (k && typeof k.identify === 'function' && typeof k.track === 'function') {
          var props = Object.assign({ $email: email }, profileProps);
          return Promise.resolve(k.identify(props))
            .catch(function () { /* still try the event */ })
            .then(function () { return k.track(eventName, eventProps); })
            .catch(function () { /* never breaks composing */ });
        }
        var q = window._learnq || (k && typeof k.push === 'function' ? k : null);
        if (q) {
          q.push(['identify', Object.assign({ $email: email }, profileProps)]);
          setTimeout(function () { try { q.push(['track', eventName, eventProps]); } catch (e) { /* fine */ } }, 1500);
          return Promise.resolve();
        }
      } catch (e) { /* never breaks composing */ }
      return Promise.resolve();
    }

    function saveProofDraft() {
      if (proofSaving) return;
      var email = (els.proofEmail.value || '').trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        els.proofStatus.classList.remove('scz-proof-status--ok');
        els.proofStatus.textContent = 'Please enter a valid email address.';
        try { els.proofEmail.focus(); } catch (e) { /* fine */ }
        return;
      }
      validateAll();
      if (!lastValidity.ok) { closeProofOffer(null); return; }
      proofSaving = true;
      els.proofSend.disabled = true;
      els.proofStatus.classList.add('scz-proof-status--ok');
      els.proofStatus.textContent = 'Preparing your proof…';
      var consent = !!(els.proofConsent && els.proofConsent.checked);
      var hash = compositionHash();
      var handle = root.getAttribute('data-product-handle') || '';
      var title = (document.querySelector('.scz-title') || {}).textContent || null;
      fontReady().then(function () {
        var dataUrl = renderProofPng(PROOF_DPI);
        var b64 = dataUrl ? dataUrl.split(',')[1] : null;
        if (b64 && b64.length > 540 * 1024) { dataUrl = renderProofPng(160); b64 = dataUrl ? dataUrl.split(',')[1] : null; }
        if (b64 && b64.length > 540 * 1024) { b64 = null; dataUrl = null; }
        var body = {
          email: email,
          marketingConsent: consent,
          catalogVersion: catalog.catalogVersion,
          composition: {
            face: currentFace, size: currentSize, placement: currentPlacement, format: format,
            lines: activeInputs().map(function (i) { return i.value; }), gaps: appliedGaps(), qty: qty(),
            measuredWith: catalog.faces[currentFace].webFont,
          },
          product: { handle: handle || null, title: title },
          image: b64 ? { base64: b64 } : null,
          visitorId: beacon.tracked() ? beacon.id() : null,
          sessionId: beacon.tracked() ? beacon.session() : null,
        };
        var controller = ('AbortController' in window) ? new AbortController() : null;
        var timer = controller && setTimeout(function () { controller.abort(); }, 12000);
        return fetch(DRAFT_URL, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body), signal: controller ? controller.signal : undefined,
        }).then(function (r) {
          if (timer) clearTimeout(timer);
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        }).then(function (d) {
          if (!d || !d.token) throw new Error('no token');
          var resumeUrl = location.origin + '/products/' + encodeURIComponent(handle) + '?draft=' + encodeURIComponent(d.token);
          var imageUrl = d.image_path ? (DRAFT_URL.replace(/\/api\/personalization\/draft$/, '') + d.image_path) : null;
          lastProofResumeUrl = resumeUrl; lastProofImageUrl = imageUrl; // the cart event carries them if the add follows
          proofSavedHash = hash;
          try { localStorage.setItem(PROOF_SAVED_KEY, hash); localStorage.setItem(PROOF_EMAIL_KEY, email); } catch (e) { /* fine */ }
          // Klaviyo: identify the profile, then the one event the proof flow
          // runs on. Consent rides as a property for the flow's filters; the
          // formal list subscription is Klaviyo-side configuration.
          klaviyoSend(email, { proof_draft_marketing_consent: consent }, 'Proof Draft Saved', {
            resume_url: resumeUrl, proof_image_url: imageUrl, design_title: title, design_handle: handle,
            quantity: qty(), face: currentFace, size: currentSize, placement: currentPlacement,
            marketing_consent: consent, draft_token: d.token, catalog_version: String(catalog.catalogVersion),
          });
          beacon.emit('proof_saved', { known: !!root.getAttribute('data-customer-email'), consent: consent, image: !!b64, qty: qty() }, { now: true });
          els.proofAsk.hidden = true; els.proofDone.hidden = false;
          els.proofDoneCopy.textContent = 'We’ve emailed a link to your draft to ' + email
            + '. Come back anytime — nothing is ordered until you check out.';
          if (dataUrl) { els.proofPreview.src = dataUrl; els.proofPreview.hidden = false; } else { els.proofPreview.hidden = true; }
          els.proofResumeLink.href = resumeUrl;
          updateProofLink();
        });
      }).catch(function () {
        els.proofStatus.classList.remove('scz-proof-status--ok');
        els.proofStatus.textContent = 'We couldn’t save that just now. Your draft is still saved in this browser — please try again in a moment.';
        els.proofSend.disabled = false;
      }).then(function () { proofSaving = false; });
    }
    if (els.proofForm) els.proofForm.addEventListener('submit', function (e) { e.preventDefault(); saveProofDraft(); });

    // Resume link: `?draft=<token>` → the server-held composition, applied
    // like a local draft, then written locally so the page behaves as if she
    // typed it here. The token leaves the URL once applied so a reload cannot
    // undo her edits.
    function restoreFromDraftToken(token) {
      fetch(DRAFT_URL + '/' + encodeURIComponent(token))
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (d) {
          var comp = d && d.composition;
          if (!comp) throw new Error('empty');
          applyComposition({ face: comp.face, size: comp.size, placement: comp.placement, lines: comp.lines || [], gaps: comp.gaps || null, qty: d.qty || comp.qty });
          ensureDefaultFace();
          clearTimeout(draftTimer); writeDraft();
          showResumeBand(d.savedAt || d.createdAt || d.created_at || null);
          beacon.emit('draft_restored', { via: 'proof_link' }, { once: true });
          try {
            var u = new URL(location.href); u.searchParams.delete('draft');
            history.replaceState(null, '', u.pathname + u.search + u.hash);
          } catch (e) { /* fine */ }
        })
        .catch(function () {
          restoreDraft(); ensureDefaultFace();
          setDraftState('That link has expired — start fresh below');
        });
    }

    // ── Proof minting (background, graceful degradation) ───────────────────
    function scheduleProof() {
      clearTimeout(proofTimer);
      proofTimer = setTimeout(mintProof, PROOF_DEBOUNCE_MS);
    }

    function mintProof() {
      if (!lastValidity.ok) return;
      if (!lastValidation || !lastValidation.ok) return;
      var seq = ++proofSeq;
      // #139: the core assembles the payload — card limitPica, bedPica and the
      // cast slugs per line — so the server's re-check and the job sheet read
      // exactly what was validated here. No `tier` key: storefront parity.
      var payload;
      try {
        payload = Core.buildProofPayload({
          catalog: catalog, composition: composition(), validation: lastValidation, limits: limits,
          product: {
            handle: root.getAttribute('data-product-handle') || null,
            variantId: (els.form.querySelector('[name="id"]') || {}).value || null,
          },
          // #81: joins proofs ↔ beacon events. Consent-gated like the beacons —
          // a declined (or unresolved) consent state sends the proof without ids,
          // so it stays functional (it becomes _proof_ref) but unjoinable.
          visitorId: beacon.tracked() ? beacon.id() : null,
          sessionId: beacon.tracked() ? beacon.session() : null,
        });
      } catch (e) { warn('proof payload: ' + e.message); return; }
      var controller = ('AbortController' in window) ? new AbortController() : null;
      var timer = controller && setTimeout(function () { controller.abort(); }, PROOF_TIMEOUT_MS);
      fetch(PROOF_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined,
      })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (d) {
          if (seq === proofSeq && d && d.proof_id) {
            setProp('_proof_ref', d.proof_id);
            proofsMinted++;
            // qty rides the proof event because the proof PAYLOAD has no
            // quantity field (it describes the composition, not the order), so
            // "were they pricing the 50-card floor or stepping up a tier?" was
            // unanswerable from stored data. Cheap here, and it makes every
            // proof a priced observation.
            beacon.emit('proof_minted', { proofId: d.proof_id, qty: qty() });
          }
        })
        .catch(function () { /* degrade: line ships without _proof_ref; ingest flags it */ })
        .then(function () { if (timer) clearTimeout(timer); });
    }

    // ── Swatch mode (?swatches=1): owner picks the red vs a printed sample ─
    function initSwatchMode() {
      if (!/[?&]swatches=1/.test(location.search)) return;
      els.swatchLabel.hidden = false;
      els.swatches.classList.add('scz-on');
      SWATCHES.forEach(function (s) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'scz-swatch';
        b.style.background = s.hex;
        b.title = s.name + ' ' + s.hex;
        b.setAttribute('aria-pressed', String(s.hex.toLowerCase() === inkHex.toLowerCase()));
        b.addEventListener('click', function () {
          inkHex = s.hex;
          els.swatches.querySelectorAll('.scz-swatch').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
          b.setAttribute('aria-pressed', 'true');
          els.swatchLabel.textContent = 'Ink: ' + s.name + ' — ' + s.hex;
          renderEmulator();
        });
        els.swatches.appendChild(b);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
